import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import api from '@/lib/api';
import {
  DEFAULT_IMPOSITION_OUTPUT_VARIABLES,
  IMPOSITION_OUTPUT_VARIABLE_DEFINITIONS,
  createImpositionOutputVariable,
  getImpositionOutputKeys,
  normalizeImpositionConfig,
} from '@/lib/impositionVariables';
import './Products.css';
import '@/components/SheetImposition.css';

const emptyVariable = (type = 'number') => ({
  key: '',
  label: '',
  type,
  unit: '',
  default_value: 0,
  options: type === 'select' ? [{ label: '', value: 0 }] : [],
  checked_value: 0,
  unchecked_value: 0,
  formula: '',
  show_in_budget: type !== 'calculated',
});

const defaultProductionTimeConfig = {
  enabled: false,
  profile_key: 'offset_principal',
  quantity_variable: 'pliegos',
  sides_multiplier_variable: '',
  default_sides_multiplier: 1,
};

const defaultPdfDetailConfig = {
  variable_keys: [],
  extra_text: '',
};

const PRODUCT_CALCULATOR_OFFSET_SIMPLE = 'offset_simple';
const PRODUCT_CALCULATOR_REVISTA = 'revista';
const PRODUCT_CALCULATOR_CUADERNO = 'cuaderno';
const PRODUCT_CALCULATOR_FACTURAS = 'facturas';

const OFFSET_SIMPLE_GENERATED_VARIABLES = [
  { key: 'chapasTotales', label: 'Chapas totales', unit: 'u' },
];

const defaultOffsetSimpleConfig = {
  front_colors_variable: '',
  back_colors_variable: '',
  plate_mode_variable: '',
  plates_total_variable: 'chapasTotales',
  same_plates_value: 1,
  different_plates_value: 2,
};

const defaultCalculatorConfig = {
  page_count_variable: '',
  pages_per_sheet_variable: DEFAULT_IMPOSITION_OUTPUT_VARIABLES.pagesPerSheet,
  distinct_sheets_variable: 'pliegosrevista',
  plates_variable: 'chapasrevista',
  faces_variable: 'carasrevista',
  print_sheets_variable: 'pliegostiradarevista',
  faces_per_sheet: 2,
  quantity_variable: 'cantidad',
  elements: [],
};

const defaultCoverConfig = {
  enabled: true,
  enabled_variable: 'tienetapa',
  front_colors_variable: 'colortapafrente',
  back_colors_variable: 'colortapadorso',
  plate_mode_variable: 'modochapastapa',
  plates_total_variable: 'chapastapa',
  same_plates_value: 1,
  different_plates_value: 2,
  print_sheets_variable: 'pliegostapa',
  mother_sheet_variable: 'hojamadretapa',
  spoilage_variable: 'desperdiciotapa',
  sheet_width_variable: 'anchohojatapa',
  sheet_height_variable: 'largohojatapa',
  pages_per_sheet_variable: 'paginasporpliegotapa',
};

const COVER_VARIABLE_DEFINITIONS = [
  { key: 'tienetapa', label: 'Tiene tapa', type: 'checkbox', unit: '', checked_value: 1, unchecked_value: 0, show_in_budget: true },
  { key: 'largotapa', label: 'Largo tapa', type: 'number', unit: 'cm', default_value: 0, show_in_budget: true },
  { key: 'anchotapa', label: 'Ancho tapa', type: 'number', unit: 'cm', default_value: 0, show_in_budget: true },
  { key: 'gramajetapa', label: 'Gramaje tapa', type: 'number', unit: 'gr', default_value: 0, show_in_budget: true },
  { key: 'papeltapa', label: 'Papel tapa', type: 'select', unit: '$', options: [{ label: 'Sin tapa', value: 0 }], show_in_budget: true },
  { key: 'colortapafrente', label: 'Colores tapa frente', type: 'number', unit: 'col.', default_value: 1, show_in_budget: true },
  { key: 'colortapadorso', label: 'Colores tapa dorso', type: 'number', unit: 'col.', default_value: 0, show_in_budget: true },
  {
    key: 'modochapastapa',
    label: 'Chapas tapa',
    type: 'select',
    unit: '',
    options: [
      { label: 'Mismas chapas frente y dorso', value: 1 },
      { label: 'Chapas distintas frente y dorso', value: 2 },
    ],
    show_in_budget: true,
  },
  { key: 'chapastapa', label: 'Chapas tapa', type: 'number', unit: 'u', default_value: 0, show_in_budget: false },
  { key: 'pliegostapa', label: 'Pliegos tapa', type: 'number', unit: 'pliegos', default_value: 0, show_in_budget: false },
  { key: 'hojamadretapa', label: 'Hojas madre tapa', type: 'number', unit: 'hojas', default_value: 0, show_in_budget: false },
  { key: 'desperdiciotapa', label: 'Desperdicio tapa', type: 'number', unit: 'hojas', default_value: 0, show_in_budget: false },
  { key: 'anchohojatapa', label: 'Ancho hoja madre tapa', type: 'number', unit: 'cm', default_value: 0, show_in_budget: false },
  { key: 'largohojatapa', label: 'Largo hoja madre tapa', type: 'number', unit: 'cm', default_value: 0, show_in_budget: false },
  { key: 'paginasporpliegotapa', label: 'Paginas por pliego tapa', type: 'number', unit: 'pag.', default_value: 0, show_in_budget: false },
  { key: 'papelcalctapa', label: 'Calculo de papel tapa', type: 'calculated', unit: '$', formula: '(tienetapa * ((((anchohojatapa * largohojatapa * gramajetapa) / 10000000) * hojamadretapa) * papeltapa))', show_in_budget: false },
  { key: 'tintacalctapa', label: 'Calculo de tinta tapa', type: 'calculated', unit: '$', formula: '((tintagramostapa / 1000) * tinta)', show_in_budget: false },
];

const COVER_FORMULA_TERMS = [
  'papelcalctapa',
  'tintacalctapa',
];

const normalizeCoverFormula = (formula = '') => {
  const cleaned = String(formula || '')
    .replace(/(^|\s*\+\s*)(costotapa|preparaciontapa|planchatapa|impresiontapa|guillotinatapa|empaquetadotapa)(\s*\+\s*|$)/g, (match, before, term, after) => {
      if (before && after) return ' + ';
      return '';
    })
    .replace(/\s+\+\s+\+/g, ' + ')
    .replace(/^\s*\+\s*|\s*\+\s*$/g, '')
    .trim();
  const missingTerms = COVER_FORMULA_TERMS.filter(term => !new RegExp(`(^|[^a-zA-Z0-9_])${term}([^a-zA-Z0-9_]|$)`).test(cleaned));
  if (missingTerms.length === 0) return cleaned;
  return `${cleaned}${cleaned ? ' + ' : ''}${missingTerms.join(' + ')}`;
};

const MAGAZINE_GENERATED_VARIABLES = [
  { key: 'pliegosrevista', label: 'Pliegos distintos revista', unit: 'pl.' },
  { key: 'chapasrevista', label: 'Chapas revista', unit: 'u' },
  { key: 'carasrevista', label: 'Caras revista', unit: 'u' },
  { key: 'pliegostiradarevista', label: 'Pliegos de tirada revista', unit: 'pliegos' },
];

const NOTEBOOK_GENERATED_VARIABLES = [
  { key: 'pliegoscuaderno', label: 'Pliegos cuaderno', unit: 'pl.' },
  { key: 'chapascuaderno', label: 'Chapas cuaderno', unit: 'u' },
  { key: 'carascuaderno', label: 'Caras cuaderno', unit: 'u' },
  { key: 'pliegostiradacuaderno', label: 'Pliegos de tirada cuaderno', unit: 'pliegos' },
];

const PAGE_CALCULATOR_CONFIG = {
  [PRODUCT_CALCULATOR_REVISTA]: {
    label: 'Revista',
    lowercaseLabel: 'revista',
    generatedVariables: MAGAZINE_GENERATED_VARIABLES,
    defaults: defaultCalculatorConfig,
    requiredPageLabel: 'Paginas revista',
  },
  [PRODUCT_CALCULATOR_CUADERNO]: {
    label: 'Cuaderno',
    lowercaseLabel: 'cuaderno',
    generatedVariables: NOTEBOOK_GENERATED_VARIABLES,
    defaults: {
      ...defaultCalculatorConfig,
      page_count_variable: 'hojas',
      pages_per_sheet_variable: 'hojasporpliego',
      distinct_sheets_variable: 'pliegoscuaderno',
      plates_variable: 'chapascuaderno',
      faces_variable: 'carascuaderno',
      print_sheets_variable: 'pliegostiradacuaderno',
      faces_per_sheet: 1,
    },
    requiredPageLabel: 'Cantidad de hojas interiores',
  },
};

const isPageCalculatorType = (type) => Boolean(PAGE_CALCULATOR_CONFIG[type]);
const getPageCalculatorMeta = (type) => PAGE_CALCULATOR_CONFIG[type] || PAGE_CALCULATOR_CONFIG[PRODUCT_CALCULATOR_REVISTA];
const getPageCalculatorDefaults = (type) => getPageCalculatorMeta(type).defaults;
const getPageGeneratedVariables = (type) => getPageCalculatorMeta(type).generatedVariables;

const INVOICE_COPY_LABELS = ['Original', 'Duplicado', 'Triplicado', 'Cuatriplicado'];
const INVOICE_COPY_TYPE_OPTIONS = INVOICE_COPY_LABELS.map((label, index) => ({
  label,
  value: index + 1,
}));

const INVOICE_GENERATED_VARIABLES = [
  { key: 'copiasfactura', label: 'Copias factura', unit: 'u' },
  { key: 'chapasfactura', label: 'Chapas factura', unit: 'u' },
  { key: 'coloresfactura', label: 'Colores factura', unit: 'col.' },
  { key: 'gruposimpresionfactura', label: 'Grupos de impresion factura', unit: 'u' },
  { key: 'pliegosfactura', label: 'Pliegos factura', unit: 'pliegos' },
  { key: 'hojamadrefactura', label: 'Hojas madre factura', unit: 'hojas' },
];

const defaultInvoiceCopy = (index, overrides = {}) => {
  const label = INVOICE_COPY_LABELS[index - 1] || `Copia ${index}`;
  const suffix = label.toLowerCase();
  return {
    key: suffix,
    label,
    paper_variable: `papel${suffix}`,
    grammage_variable: `gramaje${suffix}`,
    colors_variable: `colores${suffix}`,
    plates_variable: `chapas${suffix}`,
    active_variable: `activo${suffix}`,
    sheets_variable: `pliegos${suffix}`,
    mother_sheet_variable: `hojamadre${suffix}`,
    spoilage_variable: `desperdicio${suffix}`,
    sheet_width_variable: `anchohoja${suffix}`,
    sheet_height_variable: `largohoja${suffix}`,
    ...overrides,
  };
};

const defaultInvoiceConfig = {
  copy_count_variable: 'tipofactura',
  copy_count_output_variable: 'copiasfactura',
  plates_output_variable: 'chapasfactura',
  colors_output_variable: 'coloresfactura',
  print_groups_output_variable: 'gruposimpresionfactura',
  sheets_output_variable: 'pliegosfactura',
  mother_sheets_output_variable: 'hojamadrefactura',
  copies: [1, 2, 3, 4].map(index => defaultInvoiceCopy(index)),
};

const defaultMagazineElement = (index, overrides = {}) => ({
  enabled: false,
  label: `Interior ${index}`,
  page_count_variable: '',
  front_colors_variable: '',
  back_colors_variable: '',
  pages_per_sheet_variable: `paginasporpliegoint${index}`,
  mother_sheet_variable: `hojamadreint${index}`,
  spoilage_variable: `desperdicioint${index}`,
  sheet_width_variable: `anchohojaint${index}`,
  sheet_height_variable: `largohojaint${index}`,
  distinct_sheets_variable: `pliegosint${index}`,
  faces_variable: `carasint${index}`,
  plates_variable: `chapasint${index}`,
  print_sheets_variable: `tiradaint${index}`,
  ...overrides,
});

const normalizeMagazineElementsForEditor = (elements = []) => {
  const normalized = [1, 2].map((index) => {
    const existing = elements[index - 1] || {};
    return defaultMagazineElement(index, {
      ...existing,
      enabled: Boolean(existing.enabled || existing.page_count_variable),
      label: existing.label || `Interior ${index}`,
    });
  });
  return normalized;
};

const normalizePageElementsForEditor = (calculatorType, elements = []) => {
  const normalized = normalizeMagazineElementsForEditor(elements);
  if (calculatorType !== PRODUCT_CALCULATOR_CUADERNO) {
    return normalized;
  }
  const first = normalized[0] || defaultMagazineElement(1);
  return [
    defaultMagazineElement(1, {
      ...first,
      enabled: true,
      label: first.label && first.label !== 'Interior 1' ? first.label : 'Interior',
      page_count_variable: first.page_count_variable || 'hojas',
      pages_per_sheet_variable: first.pages_per_sheet_variable || 'hojasporpliego',
      mother_sheet_variable: first.mother_sheet_variable || 'hojamadre',
      spoilage_variable: first.spoilage_variable || 'desperdicio',
      sheet_width_variable: first.sheet_width_variable || 'anchohoja',
      sheet_height_variable: first.sheet_height_variable || 'largohoja',
      distinct_sheets_variable: first.distinct_sheets_variable || 'pliegoscuaderno',
      faces_variable: first.faces_variable || 'carascuaderno',
      plates_variable: first.plates_variable || 'chapascuaderno',
      print_sheets_variable: first.print_sheets_variable || 'pliegostiradacuaderno',
    }),
  ];
};

const normalizeInvoiceConfigForEditor = (config = {}) => ({
  ...defaultInvoiceConfig,
  ...config,
  copies: [1, 2, 3, 4].map((index) => defaultInvoiceCopy(index, (config.copies || [])[index - 1] || {})),
});

const normalizeOffsetSimpleConfigForEditor = (config = {}) => ({
  ...defaultOffsetSimpleConfig,
  ...config,
  plates_total_variable: config.plates_total_variable || defaultOffsetSimpleConfig.plates_total_variable,
  same_plates_value: Number(config.same_plates_value) || defaultOffsetSimpleConfig.same_plates_value,
  different_plates_value: Number(config.different_plates_value) || defaultOffsetSimpleConfig.different_plates_value,
});

const normalizeCoverConfigForEditor = (config = {}) => ({
  ...defaultCoverConfig,
  ...config,
  enabled: config.enabled !== false,
  same_plates_value: Number(config.same_plates_value) || defaultCoverConfig.same_plates_value,
  different_plates_value: Number(config.different_plates_value) || defaultCoverConfig.different_plates_value,
});

const createGeneratedNumberVariable = (definition) => ({
  ...emptyVariable('number'),
  key: definition.key,
  label: definition.label,
  unit: definition.unit || '',
  default_value: Number(definition.default_value) || 0,
  show_in_budget: definition.showInBudget ?? definition.show_in_budget ?? false,
});

const createCoverDefinitionVariable = (definition) => {
  if (definition.type === 'select') {
    return {
      ...emptyVariable('select'),
      key: definition.key,
      label: definition.label,
      unit: definition.unit || '',
      options: definition.options || [],
      show_in_budget: definition.show_in_budget !== false,
    };
  }
  if (definition.type === 'checkbox') {
    return {
      ...emptyVariable('checkbox'),
      key: definition.key,
      label: definition.label,
      unit: definition.unit || '',
      checked_value: Number(definition.checked_value) || 0,
      unchecked_value: Number(definition.unchecked_value) || 0,
      show_in_budget: definition.show_in_budget !== false,
    };
  }
  if (definition.type === 'calculated') {
    return {
      ...emptyVariable('calculated'),
      key: definition.key,
      label: definition.label,
      unit: definition.unit || '',
      formula: definition.formula || '',
      show_in_budget: false,
    };
  }
  return {
    ...emptyVariable('number'),
    key: definition.key,
    label: definition.label,
    unit: definition.unit || '',
    default_value: Number(definition.default_value) || 0,
    show_in_budget: definition.show_in_budget !== false,
  };
};

const createInvoiceCopyTypeVariable = (definition) => ({
  ...emptyVariable('select'),
  key: definition.key,
  label: definition.label || 'Tipo de factura',
  unit: definition.unit || 'copias',
  options: INVOICE_COPY_TYPE_OPTIONS,
  show_in_budget: true,
});

const createInvoiceDefinitionVariable = (definition) => (
  definition.type === 'select'
    ? createInvoiceCopyTypeVariable(definition)
    : createGeneratedNumberVariable(definition)
);

const buildDefaultPdfDetailConfig = (productVariables, impositionConfig = {}) => {
  const normalized = normalizeImpositionConfig(impositionConfig || {}, productVariables || []);
  const impositionKeys = new Set(getImpositionOutputKeys(normalized));
  return {
    variable_keys: (productVariables || [])
      .filter(v => v.key && v.type !== 'calculated' && v.show_in_budget !== false && !impositionKeys.has(v.key))
      .map(v => v.key),
    extra_text: '',
  };
};

const ProductEditor = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [productionType, setProductionType] = useState('');
  const [productionModels, setProductionModels] = useState([]);
  const [productCalculators, setProductCalculators] = useState([]);
  const [calculatorType, setCalculatorType] = useState(PRODUCT_CALCULATOR_OFFSET_SIMPLE);
  const [calculatorConfig, setCalculatorConfig] = useState({
    ...defaultCalculatorConfig,
    elements: normalizeMagazineElementsForEditor(),
  });
  const [variables, setVariables] = useState([]);
  const [globalVariables, setGlobalVariables] = useState([]);
  const [productionProfiles, setProductionProfiles] = useState([]);
  const [productionTimeConfig, setProductionTimeConfig] = useState(defaultProductionTimeConfig);
  const [pdfDetailConfig, setPdfDetailConfig] = useState(defaultPdfDetailConfig);
  const [formula, setFormula] = useState('');

  const [impositionConfig, setImpositionConfig] = useState({
    sheet_width: 52,
    sheet_height: 74,
    gripper_top: 2,
    gripper_bottom: 0,
    margin_left: 0,
    margin_right: 0,
    bleed: 0,
    separation: 0,
    spoilage_sheets: 50,
    allow_rotation: true,
    double_sided: false,
    width_variable: '',
    height_variable: '',
    quantity_variable: '',
    sheets_variable: DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheets,
    mother_sheet_variable: DEFAULT_IMPOSITION_OUTPUT_VARIABLES.motherSheets,
    spoilage_variable: DEFAULT_IMPOSITION_OUTPUT_VARIABLES.spoilage,
    sheet_width_variable: DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheetWidth,
    sheet_height_variable: DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheetHeight,
    pages_per_sheet_variable: DEFAULT_IMPOSITION_OUTPUT_VARIABLES.pagesPerSheet,
  });

  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [openEditorSections, setOpenEditorSections] = useState({
    details: !isEdit,
    variables: !isEdit,
    calculator: false,
    formula: !isEdit,
    pdfDetail: false,
    imposition: false,
    productionTime: false,
  });

  const toggleEditorSection = (section) => {
    setOpenEditorSections(current => ({
      ...current,
      [section]: !current[section],
    }));
  };

  const loadGlobalVariables = useCallback(async () => {
    try {
      const [{ data }, productionTimeRes, productionModelsRes, productCalculatorsRes] = await Promise.all([
        api.get('/settings/global-variables'),
        api.get('/settings/production-time'),
        api.get('/production-models'),
        api.get('/product-calculators'),
      ]);
      setGlobalVariables((data.variables || []).map(v => ({
        ...emptyVariable(v.type),
        ...v,
        options: v.options || [],
        global: true,
      })));
      setProductionProfiles(productionTimeRes.data?.profiles || []);
      setProductionModels(productionModelsRes.data || []);
      setProductCalculators(productCalculatorsRes.data || []);
    } catch (err) {
      setGlobalVariables([]);
      setProductionProfiles([]);
      setProductionModels([]);
      setProductCalculators([]);
    }
  }, []);

  const loadProduct = useCallback(async () => {
    try {
      const { data } = await api.get(`/products/${id}`);
      const productVariables = (data.variables || []).map(v => ({
        ...emptyVariable(v.type),
        ...v,
        options: v.options || [],
      }));
      setName(data.name || '');
      setDescription(data.description || '');
      setImageUrl(data.image_url || '');
      setProductionType(data.production_type || 'offset');
      const nextCalculatorType = data.calculator_type || PRODUCT_CALCULATOR_OFFSET_SIMPLE;
      setCalculatorType(nextCalculatorType);
      const pageDefaults = getPageCalculatorDefaults(nextCalculatorType);
      const nextCalculatorConfig = {
        ...pageDefaults,
        ...(data.calculator_config || {}),
      };
      nextCalculatorConfig.elements = normalizePageElementsForEditor(nextCalculatorType, nextCalculatorConfig.elements);
      nextCalculatorConfig.cover = normalizeCoverConfigForEditor(nextCalculatorConfig.cover || {});
      setCalculatorConfig(nextCalculatorType === PRODUCT_CALCULATOR_FACTURAS
        ? normalizeInvoiceConfigForEditor(data.calculator_config || {})
        : nextCalculatorType === PRODUCT_CALCULATOR_OFFSET_SIMPLE
          ? normalizeOffsetSimpleConfigForEditor(data.calculator_config || {})
        : nextCalculatorConfig);
      let nextProductVariables = productVariables;
      setFormula(data.formula || '');
      const nextProductionType = data.production_type || 'offset';
      setProductionTimeConfig({
        ...defaultProductionTimeConfig,
        ...(data.production_time_config || {}),
        enabled: nextProductionType === 'offset' ? true : Boolean(data.production_time_config?.enabled),
      });
      if (nextProductionType === 'offset') {
        const normalizedImposition = normalizeImpositionConfig(data.imposition_config || {}, productVariables);
        const requiredVariables = buildImpositionOutputDefinitions(normalizedImposition);
        const requiredByKey = new Map(requiredVariables.map(def => [def.key, def]));
        const existingKeys = new Set(productVariables.map(v => v.key).filter(Boolean));

        nextProductVariables = productVariables.map(v => {
          const definition = requiredByKey.get(v.key);
          if (!definition) return v;
          return {
            ...emptyVariable('number'),
            ...v,
            label: v.label || definition.label,
            type: 'number',
            unit: v.unit || definition.unit,
            options: [],
            formula: '',
            show_in_budget: false,
          };
        });

        requiredVariables.forEach(definition => {
          if (!existingKeys.has(definition.key)) {
            nextProductVariables.push(createImpositionOutputVariable(definition));
          }
        });

        setImpositionConfig(normalizeImpositionConfig(normalizedImposition, nextProductVariables));
      }
      if (isPageCalculatorType(nextCalculatorType)) {
        const pageMeta = getPageCalculatorMeta(nextCalculatorType);
        const generatedVariables = getPageGeneratedVariables(nextCalculatorType);
        const isNotebook = nextCalculatorType === PRODUCT_CALCULATOR_CUADERNO;
        const pageUnit = isNotebook ? 'hojas' : 'pag.';
        const pagesPerSheetLabel = isNotebook ? 'Hojas por pliego' : 'Paginas por pliego';
        const requiredVariables = [
          {
            key: nextCalculatorConfig.page_count_variable || pageDefaults.page_count_variable || 'paginas',
            label: pageMeta.requiredPageLabel,
            unit: pageUnit,
            default_value: isNotebook ? 100 : 0,
            showInBudget: isNotebook,
          },
          { ...generatedVariables[0], key: nextCalculatorConfig.distinct_sheets_variable || pageDefaults.distinct_sheets_variable },
          { ...generatedVariables[1], key: nextCalculatorConfig.plates_variable || pageDefaults.plates_variable },
          { ...generatedVariables[2], key: nextCalculatorConfig.faces_variable || pageDefaults.faces_variable },
          { ...generatedVariables[3], key: nextCalculatorConfig.print_sheets_variable || pageDefaults.print_sheets_variable },
          ...normalizePageElementsForEditor(nextCalculatorType, nextCalculatorConfig.elements)
            .filter(element => element.enabled)
            .flatMap((element, index) => {
              const label = element.label || `Interior ${index + 1}`;
              return [
                { key: element.pages_per_sheet_variable || `paginasporpliegoint${index + 1}`, label: `${pagesPerSheetLabel} ${label}`, unit: pageUnit },
                { key: element.mother_sheet_variable || `hojamadreint${index + 1}`, label: `Hojas madre ${label}`, unit: 'hojas' },
                { key: element.spoilage_variable || `desperdicioint${index + 1}`, label: `Desperdicio ${label}`, unit: 'hojas' },
                { key: element.sheet_width_variable || `anchohojaint${index + 1}`, label: `Ancho hoja madre ${label}`, unit: 'cm' },
                { key: element.sheet_height_variable || `largohojaint${index + 1}`, label: `Largo hoja madre ${label}`, unit: 'cm' },
                { key: element.distinct_sheets_variable, label: `Pliegos ${label}`, unit: 'pl.' },
                { key: element.faces_variable, label: `Caras ${label}`, unit: 'u' },
                { key: element.plates_variable, label: `Chapas ${label}`, unit: 'u' },
                { key: element.print_sheets_variable, label: `Tirada ${label}`, unit: 'pliegos' },
              ].filter(definition => definition.key);
            }),
        ];
        const requiredByKey = new Map(requiredVariables.map(def => [def.key, def]));
        const existingKeys = new Set(nextProductVariables.map(v => v.key).filter(Boolean));
        nextProductVariables = nextProductVariables.map(v => {
          const definition = requiredByKey.get(v.key);
          if (!definition) return v;
          return {
            ...emptyVariable('number'),
            ...v,
            label: v.label || definition.label,
            type: 'number',
            unit: v.unit || definition.unit,
            options: [],
            formula: '',
            show_in_budget: definition.showInBudget ?? definition.show_in_budget ?? false,
          };
        });
        requiredVariables.forEach(definition => {
          if (!existingKeys.has(definition.key)) {
            nextProductVariables.push(createGeneratedNumberVariable(definition));
          }
        });
      }
      if (nextCalculatorType === PRODUCT_CALCULATOR_FACTURAS) {
        const invoiceConfig = normalizeInvoiceConfigForEditor(data.calculator_config || {});
        const requiredVariables = [
          { key: invoiceConfig.copy_count_variable, label: 'Tipo de factura', unit: 'copias', type: 'select', showInBudget: true },
          { ...INVOICE_GENERATED_VARIABLES[0], key: invoiceConfig.copy_count_output_variable },
          { ...INVOICE_GENERATED_VARIABLES[1], key: invoiceConfig.plates_output_variable },
          { ...INVOICE_GENERATED_VARIABLES[2], key: invoiceConfig.colors_output_variable },
          { ...INVOICE_GENERATED_VARIABLES[3], key: invoiceConfig.print_groups_output_variable },
          { ...INVOICE_GENERATED_VARIABLES[4], key: invoiceConfig.sheets_output_variable },
          { ...INVOICE_GENERATED_VARIABLES[5], key: invoiceConfig.mother_sheets_output_variable },
        ].filter(definition => definition.key);
        const requiredByKey = new Map(requiredVariables.map(def => [def.key, def]));
        const existingKeys = new Set(nextProductVariables.map(v => v.key).filter(Boolean));
        nextProductVariables = nextProductVariables.map(v => {
          const definition = requiredByKey.get(v.key);
          if (!definition) return v;
          if (definition.type === 'select') {
            return {
              ...emptyVariable('select'),
              ...v,
              label: v.label || definition.label,
              type: 'select',
              unit: v.unit || definition.unit,
              options: definition.options || INVOICE_COPY_TYPE_OPTIONS,
              formula: '',
              show_in_budget: true,
            };
          }
          return {
            ...emptyVariable('number'),
            ...v,
            label: v.label || definition.label,
            type: 'number',
            unit: v.unit || definition.unit,
            options: [],
            formula: '',
            show_in_budget: definition.key === invoiceConfig.copy_count_variable,
          };
        });
        requiredVariables.forEach(definition => {
          if (!existingKeys.has(definition.key)) {
            nextProductVariables.push({
              ...createInvoiceDefinitionVariable(definition),
              show_in_budget: definition.key === invoiceConfig.copy_count_variable,
            });
          }
        });
      }
      if (nextCalculatorType === PRODUCT_CALCULATOR_OFFSET_SIMPLE) {
        const offsetConfig = normalizeOffsetSimpleConfigForEditor(data.calculator_config || {});
        const requiredVariables = OFFSET_SIMPLE_GENERATED_VARIABLES.map(definition => ({
          ...definition,
          key: offsetConfig.plates_total_variable || definition.key,
        })).filter(definition => definition.key);
        const requiredByKey = new Map(requiredVariables.map(def => [def.key, def]));
        const existingKeys = new Set(nextProductVariables.map(v => v.key).filter(Boolean));
        nextProductVariables = nextProductVariables.map(v => {
          const definition = requiredByKey.get(v.key);
          if (!definition) return v;
          return {
            ...emptyVariable('number'),
            ...v,
            label: v.label || definition.label,
            type: 'number',
            unit: v.unit || definition.unit,
            options: [],
            formula: '',
            show_in_budget: false,
          };
        });
        requiredVariables.forEach(definition => {
          if (!existingKeys.has(definition.key)) {
            nextProductVariables.push(createGeneratedNumberVariable(definition));
          }
        });
      }
      setPdfDetailConfig(data.pdf_detail_config || buildDefaultPdfDetailConfig(nextProductVariables, data.imposition_config || {}));
      setVariables(nextProductVariables);
    } catch (err) {
      setLoadError('Error al cargar el producto');
    }
  }, [id]);

  useEffect(() => {
    loadGlobalVariables();
    if (isEdit) {
      loadProduct();
    }
  }, [isEdit, loadGlobalVariables, loadProduct]);

  const addVariable = (type) => {
    setVariables([...variables, emptyVariable(type)]);
  };

  const updateVariable = (index, patch) => {
    const copy = [...variables];
    copy[index] = { ...copy[index], ...patch };
    setVariables(copy);
  };

  const removeVariable = (index) => {
    const copy = [...variables];
    copy.splice(index, 1);
    setVariables(copy);
  };

  const addOption = (varIndex) => {
    const copy = [...variables];
    copy[varIndex].options = [...(copy[varIndex].options || []), { label: '', value: 0 }];
    setVariables(copy);
  };

  const updateOption = (varIndex, optIndex, patch) => {
    const copy = [...variables];
    const opts = [...copy[varIndex].options];
    opts[optIndex] = { ...opts[optIndex], ...patch };
    copy[varIndex].options = opts;
    setVariables(copy);
  };

  const removeOption = (varIndex, optIndex) => {
    const copy = [...variables];
    const opts = [...copy[varIndex].options];
    opts.splice(optIndex, 1);
    copy[varIndex].options = opts;
    setVariables(copy);
  };

  const getMagazineOutputDefinitions = (config = calculatorConfig, type = calculatorType) => {
    const pageMeta = getPageCalculatorMeta(type);
    const pageDefaults = getPageCalculatorDefaults(type);
    const generatedVariables = getPageGeneratedVariables(type);
    const isNotebook = type === PRODUCT_CALCULATOR_CUADERNO;
    const pageUnit = isNotebook ? 'hojas' : 'pag.';
    const pagesPerSheetLabel = isNotebook ? 'Hojas por pliego' : 'Paginas por pliego';
    return [
    {
      key: config.page_count_variable || pageDefaults.page_count_variable || 'paginas',
      label: pageMeta.requiredPageLabel,
      unit: pageUnit,
      default_value: isNotebook ? 100 : 0,
      showInBudget: isNotebook,
    },
    { ...generatedVariables[0], key: config.distinct_sheets_variable || pageDefaults.distinct_sheets_variable },
    { ...generatedVariables[1], key: config.plates_variable || pageDefaults.plates_variable },
    { ...generatedVariables[2], key: config.faces_variable || pageDefaults.faces_variable },
    { ...generatedVariables[3], key: config.print_sheets_variable || pageDefaults.print_sheets_variable },
    ...(config.elements || [])
      .filter(element => element.enabled)
      .flatMap((element, index) => {
        const label = element.label || `Interior ${index + 1}`;
        return [
          { key: element.pages_per_sheet_variable || `paginasporpliegoint${index + 1}`, label: `${pagesPerSheetLabel} ${label}`, unit: pageUnit },
          { key: element.mother_sheet_variable || `hojamadreint${index + 1}`, label: `Hojas madre ${label}`, unit: 'hojas' },
          { key: element.spoilage_variable || `desperdicioint${index + 1}`, label: `Desperdicio ${label}`, unit: 'hojas' },
          { key: element.sheet_width_variable || `anchohojaint${index + 1}`, label: `Ancho hoja madre ${label}`, unit: 'cm' },
          { key: element.sheet_height_variable || `largohojaint${index + 1}`, label: `Largo hoja madre ${label}`, unit: 'cm' },
          { key: element.distinct_sheets_variable, label: `Pliegos ${label}`, unit: 'pl.' },
          { key: element.faces_variable, label: `Caras ${label}`, unit: 'u' },
          { key: element.plates_variable, label: `Chapas ${label}`, unit: 'u' },
          { key: element.print_sheets_variable, label: `Tirada ${label}`, unit: 'pliegos' },
        ].filter(definition => definition.key);
      }),
    ];
  };

  const updateMagazineElement = (index, patch) => {
    const elements = normalizePageElementsForEditor(calculatorType, calculatorConfig.elements);
    elements[index] = { ...elements[index], ...patch };
    setCalculatorConfig({ ...calculatorConfig, elements });
  };

  const ensureMagazineOutputVariables = (config = calculatorConfig) => {
    const requiredVariables = getMagazineOutputDefinitions(config);
    const requiredByKey = new Map(requiredVariables.map(def => [def.key, def]));
    const globalKeys = new Set(globalVariables.map(v => v.key).filter(Boolean));
    const productKeys = new Set(variables.map(v => v.key).filter(Boolean));

    const nextVariables = variables.map(v => {
      const definition = requiredByKey.get(v.key);
      if (!definition) return v;
      return {
        ...emptyVariable('number'),
        ...v,
        label: v.label || definition.label,
        type: 'number',
        unit: v.unit || definition.unit,
        options: [],
        formula: '',
        show_in_budget: definition.showInBudget ?? definition.show_in_budget ?? false,
      };
    });

    requiredVariables.forEach(definition => {
      if (!productKeys.has(definition.key) && !globalKeys.has(definition.key)) {
        nextVariables.push(createGeneratedNumberVariable(definition));
      }
    });

    setVariables(nextVariables);
  };

  const getInvoiceOutputDefinitions = (config = calculatorConfig) => {
    const normalized = normalizeInvoiceConfigForEditor(config);
    return [
      { key: normalized.copy_count_variable, label: 'Tipo de factura', unit: 'copias', type: 'select', options: INVOICE_COPY_TYPE_OPTIONS, showInBudget: true },
      { ...INVOICE_GENERATED_VARIABLES[0], key: normalized.copy_count_output_variable },
      { ...INVOICE_GENERATED_VARIABLES[1], key: normalized.plates_output_variable },
      { ...INVOICE_GENERATED_VARIABLES[2], key: normalized.colors_output_variable },
      { ...INVOICE_GENERATED_VARIABLES[3], key: normalized.print_groups_output_variable },
      { ...INVOICE_GENERATED_VARIABLES[4], key: normalized.sheets_output_variable },
      { ...INVOICE_GENERATED_VARIABLES[5], key: normalized.mother_sheets_output_variable },
    ].filter(definition => definition.key);
  };

  const updateInvoiceCopy = (index, patch) => {
    const current = normalizeInvoiceConfigForEditor(calculatorConfig);
    const copies = [...current.copies];
    copies[index] = { ...copies[index], ...patch };
    setCalculatorConfig({ ...current, copies });
  };

  const ensureInvoiceOutputVariables = (config = calculatorConfig) => {
    const requiredVariables = getInvoiceOutputDefinitions(config);
    const requiredByKey = new Map(requiredVariables.map(def => [def.key, def]));
    const globalKeys = new Set(globalVariables.map(v => v.key).filter(Boolean));
    const productKeys = new Set(variables.map(v => v.key).filter(Boolean));

    const nextVariables = variables.map(v => {
      const definition = requiredByKey.get(v.key);
      if (!definition) return v;
      if (definition.type === 'select') {
        return {
          ...emptyVariable('select'),
          ...v,
          label: v.label || definition.label,
          type: 'select',
          unit: v.unit || definition.unit,
          options: definition.options || INVOICE_COPY_TYPE_OPTIONS,
          formula: '',
          show_in_budget: true,
        };
      }
      return {
        ...emptyVariable('number'),
        ...v,
        label: v.label || definition.label,
        type: 'number',
        unit: v.unit || definition.unit,
        options: [],
        formula: '',
        show_in_budget: Boolean(definition.showInBudget),
      };
    });

    requiredVariables.forEach(definition => {
      if (!productKeys.has(definition.key) && !globalKeys.has(definition.key)) {
        nextVariables.push({
          ...createInvoiceDefinitionVariable(definition),
          show_in_budget: Boolean(definition.showInBudget),
        });
      }
    });

    setVariables(nextVariables);
  };

  const getOffsetSimpleOutputDefinitions = (config = calculatorConfig) => {
    const normalized = normalizeOffsetSimpleConfigForEditor(config);
    return OFFSET_SIMPLE_GENERATED_VARIABLES.map(definition => ({
      ...definition,
      key: normalized.plates_total_variable || definition.key,
    })).filter(definition => definition.key);
  };

  const ensureOffsetSimpleOutputVariables = (config = calculatorConfig) => {
    const requiredVariables = getOffsetSimpleOutputDefinitions(config);
    const requiredByKey = new Map(requiredVariables.map(def => [def.key, def]));
    const globalKeys = new Set(globalVariables.map(v => v.key).filter(Boolean));
    const productKeys = new Set(variables.map(v => v.key).filter(Boolean));

    const nextVariables = variables.map(v => {
      const definition = requiredByKey.get(v.key);
      if (!definition) return v;
      return {
        ...emptyVariable('number'),
        ...v,
        label: v.label || definition.label,
        type: 'number',
        unit: v.unit || definition.unit,
        options: [],
        formula: '',
        show_in_budget: false,
      };
    });

    requiredVariables.forEach(definition => {
      if (!productKeys.has(definition.key) && !globalKeys.has(definition.key)) {
        nextVariables.push(createGeneratedNumberVariable(definition));
      }
    });

    setVariables(nextVariables);
  };

  const buildImpositionOutputDefinitions = (config) => ([
    {
      ...IMPOSITION_OUTPUT_VARIABLE_DEFINITIONS[0],
      key: config.sheets_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheets,
    },
    {
      ...IMPOSITION_OUTPUT_VARIABLE_DEFINITIONS[1],
      key: config.mother_sheet_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.motherSheets,
    },
    {
      ...IMPOSITION_OUTPUT_VARIABLE_DEFINITIONS[2],
      key: config.spoilage_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.spoilage,
    },
    {
      ...IMPOSITION_OUTPUT_VARIABLE_DEFINITIONS[3],
      key: config.sheet_width_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheetWidth,
    },
    {
      ...IMPOSITION_OUTPUT_VARIABLE_DEFINITIONS[4],
      key: config.sheet_height_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheetHeight,
    },
    {
      ...IMPOSITION_OUTPUT_VARIABLE_DEFINITIONS[5],
      key: config.pages_per_sheet_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.pagesPerSheet,
    },
  ]);

  const ensureImpositionOutputVariables = () => {
    const normalized = normalizeImpositionConfig(impositionConfig, [...globalVariables, ...variables]);
    const requiredVariables = buildImpositionOutputDefinitions(normalized);
    const requiredByKey = new Map(requiredVariables.map(def => [def.key, def]));
    const globalKeys = new Set(globalVariables.map(v => v.key).filter(Boolean));
    const productKeys = new Set(variables.map(v => v.key).filter(Boolean));

    const nextVariables = variables.map(v => {
      const definition = requiredByKey.get(v.key);
      if (!definition) return v;
      return {
        ...emptyVariable('number'),
        ...v,
        label: v.label || definition.label,
        type: 'number',
        unit: v.unit || definition.unit,
        options: [],
        formula: '',
        show_in_budget: false,
      };
    });

    requiredVariables.forEach(definition => {
      if (!productKeys.has(definition.key) && !globalKeys.has(definition.key)) {
        nextVariables.push(createImpositionOutputVariable(definition));
      }
    });

    setVariables(nextVariables);
    setImpositionConfig(normalizeImpositionConfig(normalized, [...globalVariables, ...nextVariables]));
  };

  const handleProductionTypeChange = (nextType) => {
    setProductionType(nextType);
    const nextModel = productionModels.find(model => model.key === nextType)
      || (nextType === 'offset' ? { supports_imposition: true, supports_time: true } : null);
    if (nextModel?.supports_imposition) {
      ensureImpositionOutputVariables();
    }
    if (nextModel?.supports_time) {
      setProductionTimeConfig(current => ({ ...current, enabled: true }));
    } else {
      setProductionTimeConfig(current => ({ ...current, enabled: false }));
    }
  };

  const handleCalculatorTypeChange = (nextType) => {
    const normalized = nextType || PRODUCT_CALCULATOR_OFFSET_SIMPLE;
    setCalculatorType(normalized);
    if (isPageCalculatorType(normalized)) {
      const pageDefaults = getPageCalculatorDefaults(normalized);
      const nextConfig = {
        ...pageDefaults,
        ...calculatorConfig,
        distinct_sheets_variable: pageDefaults.distinct_sheets_variable,
        plates_variable: pageDefaults.plates_variable,
        faces_variable: pageDefaults.faces_variable,
        print_sheets_variable: pageDefaults.print_sheets_variable,
        elements: normalizePageElementsForEditor(normalized, calculatorConfig.elements),
      };
      setCalculatorConfig(nextConfig);
      ensureMagazineOutputVariables(nextConfig);
      setOpenEditorSections(current => ({ ...current, calculator: true }));
    } else if (normalized === PRODUCT_CALCULATOR_FACTURAS) {
      const nextConfig = normalizeInvoiceConfigForEditor(calculatorConfig);
      setCalculatorConfig(nextConfig);
      ensureInvoiceOutputVariables(nextConfig);
      setOpenEditorSections(current => ({ ...current, calculator: true }));
    } else if (normalized === PRODUCT_CALCULATOR_OFFSET_SIMPLE) {
      const nextConfig = normalizeOffsetSimpleConfigForEditor(calculatorConfig);
      setCalculatorConfig(nextConfig);
      ensureOffsetSimpleOutputVariables(nextConfig);
      setOpenEditorSections(current => ({ ...current, calculator: true }));
    }
  };

  const buildPayload = () => {
    const isOffsetProduct = selectedProductionModel.supports_imposition;
    const isMagazineProduct = isPageCalculatorType(calculatorType);
    const isInvoiceProduct = calculatorType === PRODUCT_CALCULATOR_FACTURAS;
    const isOffsetSimpleProduct = calculatorType === PRODUCT_CALCULATOR_OFFSET_SIMPLE;
    const normalizedImpositionConfig = isOffsetProduct
      ? normalizeImpositionConfig(impositionConfig, [...globalVariables, ...variables])
      : null;
    const impositionOutputKeysForPayload = new Set(
      normalizedImpositionConfig ? getImpositionOutputKeys(normalizedImpositionConfig) : []
    );
    const magazineOutputDefinitions = isMagazineProduct ? getMagazineOutputDefinitions(calculatorConfig) : [];
    const magazineOutputKeysForPayload = new Set(magazineOutputDefinitions.map(def => def.key));
    const coverDefinitions = isMagazineProduct ? COVER_VARIABLE_DEFINITIONS : [];
    const coverDefinitionKeysForPayload = new Set(coverDefinitions.map(def => def.key));
    const invoiceOutputDefinitions = isInvoiceProduct ? getInvoiceOutputDefinitions(calculatorConfig) : [];
    const invoiceOutputKeysForPayload = new Set(invoiceOutputDefinitions.map(def => def.key));
    const offsetSimpleOutputDefinitions = isOffsetSimpleProduct ? getOffsetSimpleOutputDefinitions(calculatorConfig) : [];
    const offsetSimpleOutputKeysForPayload = new Set(offsetSimpleOutputDefinitions.map(def => def.key));
    let variablesForPayload = variables;
    if (normalizedImpositionConfig) {
      const requiredVariables = buildImpositionOutputDefinitions(normalizedImpositionConfig);
      const existingKeys = new Set([
        ...globalVariables.map(v => v.key).filter(Boolean),
        ...variables.map(v => v.key).filter(Boolean),
      ]);
      const missingGeneratedVariables = requiredVariables
        .filter(definition => !existingKeys.has(definition.key))
        .map(createImpositionOutputVariable);
      variablesForPayload = [...variables, ...missingGeneratedVariables];
    }
    if (isMagazineProduct) {
      const existingKeys = new Set([
        ...globalVariables.map(v => v.key).filter(Boolean),
        ...variablesForPayload.map(v => v.key).filter(Boolean),
      ]);
      const missingGeneratedVariables = magazineOutputDefinitions
        .filter(definition => !existingKeys.has(definition.key))
        .map(createGeneratedNumberVariable);
      variablesForPayload = [...variablesForPayload, ...missingGeneratedVariables];

      const existingWithMagazineKeys = new Set([
        ...globalVariables.map(v => v.key).filter(Boolean),
        ...variablesForPayload.map(v => v.key).filter(Boolean),
      ]);
      const missingCoverVariables = coverDefinitions
        .filter(definition => !existingWithMagazineKeys.has(definition.key))
        .map(createCoverDefinitionVariable);
      variablesForPayload = [...variablesForPayload, ...missingCoverVariables];
    }
    if (isInvoiceProduct) {
      const existingKeys = new Set([
        ...globalVariables.map(v => v.key).filter(Boolean),
        ...variablesForPayload.map(v => v.key).filter(Boolean),
      ]);
      const missingGeneratedVariables = invoiceOutputDefinitions
        .filter(definition => !existingKeys.has(definition.key))
        .map(definition => ({
          ...createInvoiceDefinitionVariable(definition),
          show_in_budget: Boolean(definition.showInBudget),
        }));
      variablesForPayload = [...variablesForPayload, ...missingGeneratedVariables];
    }
    if (isOffsetSimpleProduct) {
      const existingKeys = new Set([
        ...globalVariables.map(v => v.key).filter(Boolean),
        ...variablesForPayload.map(v => v.key).filter(Boolean),
      ]);
      const missingGeneratedVariables = offsetSimpleOutputDefinitions
        .filter(definition => !existingKeys.has(definition.key))
        .map(createGeneratedNumberVariable);
      variablesForPayload = [...variablesForPayload, ...missingGeneratedVariables];
    }
    const invoiceConfig = normalizeInvoiceConfigForEditor(calculatorConfig);
    const offsetSimpleConfig = normalizeOffsetSimpleConfigForEditor(calculatorConfig);
    const pageDefaultsForPayload = getPageCalculatorDefaults(calculatorType);
    const normalizedFormula = formula.trim();
    const formulaForPayload = isMagazineProduct ? normalizeCoverFormula(normalizedFormula) : normalizedFormula;
    const payload = {
      name: name.trim(),
      description: description.trim(),
      image_url: imageUrl.trim(),
      production_type: productionType,
      calculator_type: calculatorType || PRODUCT_CALCULATOR_OFFSET_SIMPLE,
      calculator_config: isMagazineProduct
        ? {
          page_count_variable: calculatorConfig.page_count_variable || pageDefaultsForPayload.page_count_variable || 'paginas',
          pages_per_sheet_variable: normalizedImpositionConfig?.pages_per_sheet_variable || calculatorConfig.pages_per_sheet_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.pagesPerSheet,
          distinct_sheets_variable: calculatorConfig.distinct_sheets_variable || pageDefaultsForPayload.distinct_sheets_variable,
          plates_variable: calculatorConfig.plates_variable || pageDefaultsForPayload.plates_variable,
          faces_variable: calculatorConfig.faces_variable || pageDefaultsForPayload.faces_variable,
          print_sheets_variable: calculatorConfig.print_sheets_variable || pageDefaultsForPayload.print_sheets_variable,
          faces_per_sheet: Number(calculatorConfig.faces_per_sheet) || Number(pageDefaultsForPayload.faces_per_sheet) || 2,
          quantity_variable: calculatorConfig.quantity_variable || pageDefaultsForPayload.quantity_variable || 'cantidad',
          cover: normalizeCoverConfigForEditor(calculatorConfig.cover || {}),
          elements: normalizePageElementsForEditor(calculatorType, calculatorConfig.elements)
            .filter(element => element.enabled)
            .map((element, index) => ({
              label: element.label || '',
              page_count_variable: element.page_count_variable || '',
              front_colors_variable: element.front_colors_variable || '',
              back_colors_variable: element.back_colors_variable || '',
              pages_per_sheet_variable: element.pages_per_sheet_variable || `paginasporpliegoint${index + 1}`,
              mother_sheet_variable: element.mother_sheet_variable || `hojamadreint${index + 1}`,
              spoilage_variable: element.spoilage_variable || `desperdicioint${index + 1}`,
              sheet_width_variable: element.sheet_width_variable || `anchohojaint${index + 1}`,
              sheet_height_variable: element.sheet_height_variable || `largohojaint${index + 1}`,
              distinct_sheets_variable: element.distinct_sheets_variable || '',
              faces_variable: element.faces_variable || '',
              plates_variable: element.plates_variable || '',
              print_sheets_variable: element.print_sheets_variable || '',
            })),
        }
        : isInvoiceProduct
          ? {
            copy_count_variable: invoiceConfig.copy_count_variable || defaultInvoiceConfig.copy_count_variable,
            copy_count_output_variable: invoiceConfig.copy_count_output_variable || defaultInvoiceConfig.copy_count_output_variable,
            plates_output_variable: invoiceConfig.plates_output_variable || defaultInvoiceConfig.plates_output_variable,
            colors_output_variable: invoiceConfig.colors_output_variable || defaultInvoiceConfig.colors_output_variable,
            print_groups_output_variable: invoiceConfig.print_groups_output_variable || defaultInvoiceConfig.print_groups_output_variable,
            sheets_output_variable: invoiceConfig.sheets_output_variable || defaultInvoiceConfig.sheets_output_variable,
            mother_sheets_output_variable: invoiceConfig.mother_sheets_output_variable || defaultInvoiceConfig.mother_sheets_output_variable,
            copies: invoiceConfig.copies.map(copy => ({
              key: copy.key || '',
              label: copy.label || '',
              paper_variable: copy.paper_variable || '',
              grammage_variable: copy.grammage_variable || '',
              colors_variable: copy.colors_variable || '',
              plates_variable: copy.plates_variable || '',
              active_variable: copy.active_variable || '',
              sheets_variable: copy.sheets_variable || '',
              mother_sheet_variable: copy.mother_sheet_variable || '',
              spoilage_variable: copy.spoilage_variable || '',
              sheet_width_variable: copy.sheet_width_variable || '',
              sheet_height_variable: copy.sheet_height_variable || '',
            })),
          }
        : isOffsetSimpleProduct
          ? {
            front_colors_variable: offsetSimpleConfig.front_colors_variable || '',
            back_colors_variable: offsetSimpleConfig.back_colors_variable || '',
            plate_mode_variable: offsetSimpleConfig.plate_mode_variable || '',
            plates_total_variable: offsetSimpleConfig.plates_total_variable || defaultOffsetSimpleConfig.plates_total_variable,
            same_plates_value: Number(offsetSimpleConfig.same_plates_value) || defaultOffsetSimpleConfig.same_plates_value,
            different_plates_value: Number(offsetSimpleConfig.different_plates_value) || defaultOffsetSimpleConfig.different_plates_value,
          }
        : {},
      formula: formulaForPayload,
      profit_percentage: 0,
      variables: variablesForPayload.map(v => {
        const base = {
          key: v.key.trim(),
          label: v.label.trim(),
          type: v.type,
          unit: v.unit || '',
          show_in_budget: v.type === 'calculated' || impositionOutputKeysForPayload.has(v.key) || magazineOutputKeysForPayload.has(v.key)
            || (coverDefinitionKeysForPayload.has(v.key) && COVER_VARIABLE_DEFINITIONS.find(definition => definition.key === v.key)?.show_in_budget === false)
            || offsetSimpleOutputKeysForPayload.has(v.key)
            || (invoiceOutputKeysForPayload.has(v.key) && v.key !== invoiceConfig.copy_count_variable)
            ? false
            : v.show_in_budget !== false,
        };
        if (v.type === 'number') {
          return { ...base, default_value: Number(v.default_value) || 0 };
        }
        if (v.type === 'select') {
          return {
            ...base,
            options: (v.options || []).map(o => ({ label: o.label, value: Number(o.value) || 0 })),
          };
        }
        if (v.type === 'checkbox') {
          return {
            ...base,
            checked_value: Number(v.checked_value) || 0,
            unchecked_value: Number(v.unchecked_value) || 0,
          };
        }
        if (v.type === 'calculated') {
          return {
            ...base,
            formula: v.formula || '',
          };
        }
        return base;
      }),
    };
    if (isOffsetProduct) {
      payload.imposition_config = {
        sheet_width: Number(normalizedImpositionConfig.sheet_width) || 0,
        sheet_height: Number(normalizedImpositionConfig.sheet_height) || 0,
        gripper_top: Number(normalizedImpositionConfig.gripper_top) || 0,
        gripper_bottom: Number(normalizedImpositionConfig.gripper_bottom) || 0,
        margin_left: Number(normalizedImpositionConfig.margin_left) || 0,
        margin_right: Number(normalizedImpositionConfig.margin_right) || 0,
        bleed: Number(normalizedImpositionConfig.bleed) || 0,
        separation: Number(normalizedImpositionConfig.separation) || 0,
        spoilage_sheets: Number(normalizedImpositionConfig.spoilage_sheets) || 0,
        allow_rotation: Boolean(normalizedImpositionConfig.allow_rotation),
        double_sided: Boolean(normalizedImpositionConfig.double_sided),
        width_variable: normalizedImpositionConfig.width_variable || '',
        height_variable: normalizedImpositionConfig.height_variable || '',
        quantity_variable: normalizedImpositionConfig.quantity_variable || '',
        sheets_variable: normalizedImpositionConfig.sheets_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheets,
        mother_sheet_variable: normalizedImpositionConfig.mother_sheet_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.motherSheets,
        spoilage_variable: normalizedImpositionConfig.spoilage_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.spoilage,
        sheet_width_variable: normalizedImpositionConfig.sheet_width_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheetWidth,
        sheet_height_variable: normalizedImpositionConfig.sheet_height_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheetHeight,
        pages_per_sheet_variable: normalizedImpositionConfig.pages_per_sheet_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.pagesPerSheet,
      };
    } else {
      payload.imposition_config = null;
    }
    payload.production_time_config = {
      enabled: Boolean(selectedProductionModel.supports_time),
      profile_key: productionTimeConfig.profile_key || productionProfiles[0]?.key || 'offset_principal',
      quantity_variable: productionTimeConfig.quantity_variable || 'pliegos',
      sides_multiplier_variable: productionTimeConfig.sides_multiplier_variable || '',
      default_sides_multiplier: Number(productionTimeConfig.default_sides_multiplier) || 1,
    };
    payload.pdf_detail_config = {
      variable_keys: (pdfDetailConfig.variable_keys || []).filter(Boolean),
      extra_text: (pdfDetailConfig.extra_text || '').trim(),
    };
    return payload;
  };

  const allVariables = [...globalVariables, ...variables];
  const selectedProductionModel = productionModels.find(model => model.key === productionType)
    || (productionType === 'offset'
      ? { key: 'offset', label: 'Offset', implemented: true, supports_imposition: true, supports_time: true }
      : { key: '', label: '', implemented: false, supports_imposition: false, supports_time: false });
  const availableProductCalculators = productCalculators.length
    ? productCalculators
    : [
      { key: PRODUCT_CALCULATOR_OFFSET_SIMPLE, label: 'Offset simple', production_type: 'offset' },
      { key: PRODUCT_CALCULATOR_REVISTA, label: 'Revista', production_type: 'offset' },
      { key: PRODUCT_CALCULATOR_CUADERNO, label: 'Cuaderno', production_type: 'offset' },
      { key: PRODUCT_CALCULATOR_FACTURAS, label: 'Facturas', production_type: 'offset' },
    ];
  const calculatorsForModel = availableProductCalculators.filter(calc => (
    !calc.production_type || calc.production_type === productionType
  ));
  const selectedCalculator = availableProductCalculators.find(calc => calc.key === calculatorType)
    || availableProductCalculators[0];
  const supportsOffsetImposition = selectedProductionModel.supports_imposition;
  const supportsProductionTime = selectedProductionModel.supports_time;
  const selectedProductionProfile = productionProfiles.find(profile => profile.key === productionTimeConfig.profile_key);
  const productionGeneratedVariables = selectedProductionProfile?.generated_variables || [
    { key: 'tiempo', label: 'Tiempo de produccion', type: 'number', unit: 'h', generated: true },
    { key: 'tiempo_minutos', label: 'Tiempo de produccion', type: 'number', unit: 'min', generated: true },
  ];
  const offsetSimpleGeneratedVariables = calculatorType === PRODUCT_CALCULATOR_OFFSET_SIMPLE
    ? getOffsetSimpleOutputDefinitions(calculatorConfig).map(v => ({ ...v, type: 'number', generated: true }))
    : [];
  const formulaReferenceVariables = supportsProductionTime
    ? [
      ...allVariables,
      ...offsetSimpleGeneratedVariables,
      ...productionGeneratedVariables.map(v => ({ ...v, generated: true })),
    ]
    : [
      ...allVariables,
      ...offsetSimpleGeneratedVariables,
    ];
  const numericVariables = allVariables.filter(v => v.key && v.type === 'number');
  const impositionOutputKeys = new Set(getImpositionOutputKeys(impositionConfig));
  const magazineOutputKeys = new Set(
    isPageCalculatorType(calculatorType)
      ? getMagazineOutputDefinitions(calculatorConfig, calculatorType).map(def => def.key)
      : []
  );
  const invoiceOutputKeys = new Set(
    calculatorType === PRODUCT_CALCULATOR_FACTURAS
      ? getInvoiceOutputDefinitions(calculatorConfig)
        .filter(def => !def.showInBudget)
        .map(def => def.key)
      : []
  );
  const offsetSimpleOutputKeys = new Set(
    calculatorType === PRODUCT_CALCULATOR_OFFSET_SIMPLE
      ? getOffsetSimpleOutputDefinitions(calculatorConfig).map(def => def.key)
      : []
  );
  const lockedOutputKeys = new Set([...impositionOutputKeys, ...magazineOutputKeys, ...invoiceOutputKeys, ...offsetSimpleOutputKeys]);
  const inputNumericVariables = numericVariables.filter(v => !lockedOutputKeys.has(v.key));
  const pdfDetailVariableCandidates = allVariables.filter(v => (
    v.key
    && v.type !== 'calculated'
    && !lockedOutputKeys.has(v.key)
  ));

  const togglePdfDetailVariable = (key) => {
    setPdfDetailConfig(current => {
      const selected = new Set(current.variable_keys || []);
      if (selected.has(key)) {
        selected.delete(key);
      } else {
        selected.add(key);
      }
      return { ...current, variable_keys: Array.from(selected) };
    });
  };

  const handleSave = async () => {
    setSaveError('');
    if (!name.trim()) {
      setSaveError('El nombre es obligatorio');
      return;
    }
    if (!productionType) {
      setSaveError('El modelo de produccion es obligatorio');
      return;
    }
    if (supportsOffsetImposition && (
      !impositionConfig.width_variable ||
      !impositionConfig.height_variable ||
      !impositionConfig.quantity_variable ||
      !impositionConfig.sheets_variable ||
      !impositionConfig.mother_sheet_variable ||
      !impositionConfig.spoilage_variable ||
      !impositionConfig.sheet_width_variable ||
      !impositionConfig.sheet_height_variable ||
      !impositionConfig.pages_per_sheet_variable
    )) {
      setSaveError('La configuracion de imposicion Offset es obligatoria');
      return;
    }
    if (supportsProductionTime && !productionTimeConfig.quantity_variable) {
      setSaveError('La variable de cantidad para tiempo de produccion es obligatoria');
      return;
    }
    const activeMagazineElements = normalizePageElementsForEditor(calculatorType, calculatorConfig.elements)
      .filter(element => element.enabled);
    const hasInvalidMagazineElement = activeMagazineElements.some(element => (
      !element.page_count_variable ||
      !element.front_colors_variable ||
      (calculatorType !== PRODUCT_CALCULATOR_CUADERNO && !element.back_colors_variable)
    ));
    if (isPageCalculatorType(calculatorType) && (
      !calculatorConfig.pages_per_sheet_variable ||
      !calculatorConfig.distinct_sheets_variable ||
      !calculatorConfig.plates_variable ||
      !calculatorConfig.faces_variable ||
      !calculatorConfig.print_sheets_variable ||
      activeMagazineElements.length === 0 ||
      hasInvalidMagazineElement
    )) {
      setSaveError(`La configuracion del calculador ${getPageCalculatorMeta(calculatorType).label} es obligatoria`);
      return;
    }
    const invoiceConfig = normalizeInvoiceConfigForEditor(calculatorConfig);
    const hasInvalidInvoiceCopy = invoiceConfig.copies.some(copy => (
      !copy.paper_variable ||
      !copy.colors_variable ||
      !copy.plates_variable
    ));
    if (calculatorType === PRODUCT_CALCULATOR_FACTURAS && (
      !invoiceConfig.copy_count_variable ||
      !invoiceConfig.copy_count_output_variable ||
      !invoiceConfig.plates_output_variable ||
      !invoiceConfig.colors_output_variable ||
      !invoiceConfig.print_groups_output_variable ||
      hasInvalidInvoiceCopy
    )) {
      setSaveError('La configuracion del calculador Facturas es obligatoria');
      return;
    }
    if (!formula.trim()) {
      setSaveError('La formula es obligatoria');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await api.put(`/products/${id}`, payload);
      } else {
        await api.post('/products', payload);
      }
      navigate('/productos');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setSaveError(typeof detail === 'string' ? detail : 'Error al guardar el producto');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar activePath={location.pathname} />

      <main className="main-content">
        <div className="page-header-row">
          <div>
            <h1 data-testid="editor-title">{isEdit ? 'Editar Producto' : 'Nuevo Producto'}</h1>
            <p className="dashboard-subtitle">
              Defini las variables y la formula antes de guardar
            </p>
          </div>
          <button
            className="btn-secondary"
            onClick={() => navigate('/productos')}
            data-testid="cancel-button"
          >
            Volver
          </button>
        </div>

        {loadError && <div className="login-error">{loadError}</div>}

        {/* ===== BASIC INFO ===== */}
        <div className="card editor-card">
          <button
            type="button"
            className="settings-section-header editor-section-header"
            onClick={() => toggleEditorSection('details')}
          >
            <span>
              <span className="settings-section-title">Datos del Producto</span>
              <span className="settings-section-subtitle">
                Nombre, modelo, tipo de calculo y foto del producto.
              </span>
            </span>
            <span className="settings-section-toggle">
              {openEditorSections.details ? 'Guardar' : 'Desplegar'}
            </span>
          </button>

          {openEditorSections.details ? (
          <div className="settings-section-body">
          <div className="form-group">
            <label className="form-label">Nombre *</label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Afiches"
              data-testid="product-name-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Descripcion</label>
            <input
              type="text"
              className="form-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breve descripcion del producto"
              data-testid="product-description-input"
            />
          </div>

          <div className="variable-row">
            <div className="form-group">
              <label className="form-label">Modelo de produccion *</label>
              <select
                className="form-input"
                value={productionType}
                onChange={(e) => handleProductionTypeChange(e.target.value)}
                data-testid="product-production-type-select"
              >
                <option value="">-- Elegir modelo --</option>
                {(productionModels.length ? productionModels : [{ key: 'offset', label: 'Offset' }]).map(model => (
                  <option key={model.key} value={model.key}>
                    {model.label}{model.implemented === false ? ' (pendiente)' : ''}
                  </option>
                ))}
              </select>
              <div className="input-hint">
                Cada modelo tiene sus propias reglas y configuraciones.
              </div>
            </div>
            {productionType === 'offset' && (
              <div className="form-group">
                <label className="form-label">Tipo de calculo *</label>
                <select
                  className="form-input"
                  value={calculatorType}
                  onChange={(e) => handleCalculatorTypeChange(e.target.value)}
                >
                  {calculatorsForModel.map(calculator => (
                    <option key={calculator.key} value={calculator.key}>
                      {calculator.label}
                    </option>
                  ))}
                </select>
                <div className="input-hint">
                  Cada tipo de calculo mantiene sus reglas propias. Cuaderno calcula por hojas y usa una chapa repetitiva.
                </div>
              </div>
            )}
          </div>

          <div className="variable-row">
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Foto del producto</label>
              <input
                type="url"
                className="form-input"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://..."
                data-testid="product-image-url-input"
              />
              <div className="input-hint">Pega la URL de una imagen para identificar visualmente el producto.</div>
            </div>
            <div className="product-image-preview">
              {imageUrl ? (
                <img src={imageUrl} alt={name || 'Producto'} />
              ) : (
                <span>Sin foto</span>
              )}
            </div>
          </div>

          </div>
          ) : (
          <div className="settings-section-summary editor-section-summary">
            <span>{name || 'Sin nombre'}</span>
            <span>{selectedProductionModel.label || 'Sin modelo'}</span>
            <span>{selectedCalculator?.label || 'Offset simple'}</span>
            <span>{imageUrl ? 'Con foto' : 'Sin foto'}</span>
          </div>
          )}
        </div>

        {/* ===== VARIABLES ===== */}
        <div className="card editor-card">
          <button
            type="button"
            className="settings-section-header editor-section-header"
            onClick={() => toggleEditorSection('variables')}
          >
            <span>
              <span className="settings-section-title">Variables</span>
              <span className="settings-section-subtitle">
                Campos, selecciones y variables calculadas del producto.
              </span>
            </span>
            <span className="settings-section-toggle">
              {openEditorSections.variables ? 'Guardar' : 'Desplegar'}
            </span>
          </button>

          {openEditorSections.variables ? (
          <div className="settings-section-body">
            <div className="add-variable-buttons">
              <button className="btn-small" onClick={() => addVariable('number')} data-testid="add-number-var">+ Numero</button>
              <button className="btn-small" onClick={() => addVariable('select')} data-testid="add-select-var">+ Seleccion</button>
              <button className="btn-small" onClick={() => addVariable('checkbox')} data-testid="add-checkbox-var">+ Casilla</button>
              <button className="btn-small" onClick={() => addVariable('calculated')} data-testid="add-calculated-var">+ Calculada</button>
            </div>

          {variables.length === 0 && (
            <div className="empty-state">
              Aun no hay variables. Agrega al menos una para poder construir la formula.
            </div>
          )}

          {variables.map((v, idx) => {
            const lockedByImposition = lockedOutputKeys.has(v.key);
            return (
            <div key={idx} className="variable-card" data-testid={`variable-${idx}`}>
              <div className="variable-header">
                <span className="variable-type-badge">{v.type}</span>
                <div className="variable-header-actions">
                  <div className="visibility-toggle" aria-label="Visibilidad en presupuesto">
                    <button
                      type="button"
                      className={`visibility-toggle-btn ${!lockedByImposition && v.type !== 'calculated' && v.show_in_budget !== false ? 'active' : ''}`}
                      disabled={lockedByImposition || v.type === 'calculated'}
                      onClick={() => updateVariable(idx, { show_in_budget: true })}
                    >
                      Visible
                    </button>
                    <button
                      type="button"
                      className={`visibility-toggle-btn ${lockedByImposition || v.type === 'calculated' || v.show_in_budget === false ? 'active' : ''}`}
                      disabled={lockedByImposition}
                      onClick={() => updateVariable(idx, { show_in_budget: false })}
                    >
                      Oculta
                    </button>
                  </div>
                  <button
                    className="btn-remove"
                    onClick={() => removeVariable(idx)}
                    disabled={lockedByImposition}
                    title={lockedByImposition ? 'Variable generada por imposicion' : ''}
                    data-testid={`remove-variable-${idx}`}
                  >
                    x
                  </button>
                </div>
              </div>

              <div className="variable-row">
                <div className="form-group">
                  <label className="form-label">Identificador *</label>
                  <input
                    type="text"
                    className="form-input mono"
                    value={v.key}
                    onChange={(e) => updateVariable(idx, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
                    placeholder="largo"
                    data-testid={`variable-key-${idx}`}
                  />
                  <div className="input-hint">Usar en la formula: sin espacios, solo letras/numeros/_</div>
                </div>

                <div className="form-group">
                  <label className="form-label">Etiqueta *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={v.label}
                    onChange={(e) => updateVariable(idx, { label: e.target.value })}
                    placeholder="Largo"
                    data-testid={`variable-label-${idx}`}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Unidad (opcional)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={v.unit || ''}
                    onChange={(e) => updateVariable(idx, { unit: e.target.value })}
                    placeholder="cm, unidades, gr"
                  />
                </div>
              </div>

              {v.type === 'number' && (
                <div className="form-group">
                  <label className="form-label">Valor por defecto</label>
                  <input
                    type="number"
                    className="form-input"
                    value={v.default_value}
                    onChange={(e) => updateVariable(idx, { default_value: e.target.value })}
                    data-testid={`variable-default-${idx}`}
                  />
                </div>
              )}

              {v.type === 'select' && (
                <div className="form-group">
                  <label className="form-label">Opciones (cada opcion con su precio)</label>
                  {(v.options || []).map((opt, oi) => (
                    <div key={oi} className="option-row">
                      <input
                        type="text"
                        className="form-input"
                        value={opt.label}
                        onChange={(e) => updateOption(idx, oi, { label: e.target.value })}
                        placeholder="Nombre (ej: 80gr)"
                        data-testid={`variable-${idx}-option-label-${oi}`}
                      />
                      <input
                        type="number"
                        className="form-input mono"
                        value={opt.value}
                        onChange={(e) => updateOption(idx, oi, { value: e.target.value })}
                        placeholder="Precio"
                        step="0.01"
                        data-testid={`variable-${idx}-option-value-${oi}`}
                      />
                      <button className="btn-remove" onClick={() => removeOption(idx, oi)}>x</button>
                    </div>
                  ))}
                  <button className="btn-small" onClick={() => addOption(idx)} data-testid={`add-option-${idx}`}>+ Opcion</button>
                </div>
              )}

              {v.type === 'checkbox' && (
                <div className="variable-row">
                  <div className="form-group">
                    <label className="form-label">Valor si esta marcada</label>
                    <input
                      type="number"
                      className="form-input mono"
                      value={v.checked_value}
                      onChange={(e) => updateVariable(idx, { checked_value: e.target.value })}
                      step="0.01"
                      data-testid={`variable-checked-${idx}`}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Valor si esta desmarcada</label>
                    <input
                      type="number"
                      className="form-input mono"
                      value={v.unchecked_value}
                      onChange={(e) => updateVariable(idx, { unchecked_value: e.target.value })}
                      step="0.01"
                    />
                  </div>
                </div>
              )}

              {v.type === 'calculated' && (
                <div className="form-group">
                  <label className="form-label">Formula de la variable</label>
                  <textarea
                    className="form-input mono formula-input"
                    value={v.formula || ''}
                    onChange={(e) => updateVariable(idx, { formula: e.target.value })}
                    placeholder="Ej: cantidad * 1500"
                    rows={2}
                  />
                  <div className="input-hint">
                    No se carga en el presupuesto. Se calcula y queda disponible por su identificador.
                  </div>
                </div>
              )}
            </div>
          );
          })}
          </div>
          ) : (
          <div className="settings-section-summary editor-section-summary">
            <span>{variables.length} variables cargadas</span>
            <span>{variables.filter(v => v.type === 'calculated').length} calculadas</span>
            <span>{variables.filter(v => v.show_in_budget !== false && v.type !== 'calculated').length} visibles en presupuesto</span>
          </div>
          )}
        </div>

        {/* ===== PRODUCT CALCULATOR ===== */}
        {calculatorType === PRODUCT_CALCULATOR_OFFSET_SIMPLE && (
        <div className="card editor-card">
          <button
            type="button"
            className="settings-section-header editor-section-header"
            onClick={() => toggleEditorSection('calculator')}
          >
            <span>
              <span className="settings-section-title">Calculador Offset simple</span>
              <span className="settings-section-subtitle">
                Genera chapasTotales segun si frente y dorso comparten chapas.
              </span>
            </span>
            <span className="settings-section-toggle">
              {openEditorSections.calculator ? 'Guardar' : 'Desplegar'}
            </span>
          </button>

          {openEditorSections.calculator ? (
          <div className="settings-section-body">
            <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
              La formula puede usar una sola variable: chapasTotales. El calculador decide si toma solo colores frente o suma frente y dorso.
            </p>
            <div className="variable-row">
              <div className="form-group">
                <label className="form-label">Variable colores frente</label>
                <select
                  className="form-input"
                  value={normalizeOffsetSimpleConfigForEditor(calculatorConfig).front_colors_variable}
                  onChange={(e) => setCalculatorConfig({
                    ...normalizeOffsetSimpleConfigForEditor(calculatorConfig),
                    front_colors_variable: e.target.value,
                  })}
                >
                  <option value="">-- Elegir --</option>
                  {inputNumericVariables.map(v => (
                    <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                      {v.label || v.key}{v.global ? ' (global)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Variable colores dorso</label>
                <select
                  className="form-input"
                  value={normalizeOffsetSimpleConfigForEditor(calculatorConfig).back_colors_variable}
                  onChange={(e) => setCalculatorConfig({
                    ...normalizeOffsetSimpleConfigForEditor(calculatorConfig),
                    back_colors_variable: e.target.value,
                  })}
                >
                  <option value="">-- Elegir --</option>
                  {inputNumericVariables.map(v => (
                    <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                      {v.label || v.key}{v.global ? ' (global)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Variable modo de chapas</label>
                <select
                  className="form-input"
                  value={normalizeOffsetSimpleConfigForEditor(calculatorConfig).plate_mode_variable}
                  onChange={(e) => setCalculatorConfig({
                    ...normalizeOffsetSimpleConfigForEditor(calculatorConfig),
                    plate_mode_variable: e.target.value,
                  })}
                >
                  <option value="">-- Elegir --</option>
                  {allVariables.filter(v => v.key && v.type === 'select').map(v => (
                    <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                      {v.label || v.key}{v.global ? ' (global)' : ''}
                    </option>
                  ))}
                </select>
                <div className="input-hint">Usa 1 para mismas chapas y 2 para chapas distintas.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Variable total chapas</label>
                <input
                  className="form-input mono"
                  value={normalizeOffsetSimpleConfigForEditor(calculatorConfig).plates_total_variable}
                  onChange={(e) => setCalculatorConfig({
                    ...normalizeOffsetSimpleConfigForEditor(calculatorConfig),
                    plates_total_variable: e.target.value,
                  })}
                />
                <div className="input-hint">Debe usarse en la formula como chapasTotales.</div>
              </div>
            </div>
            <button
              type="button"
              className="btn-small"
              onClick={() => ensureOffsetSimpleOutputVariables(calculatorConfig)}
            >
              Generar variable
            </button>
          </div>
          ) : (
          <div className="settings-section-summary editor-section-summary">
            <span>Frente: {normalizeOffsetSimpleConfigForEditor(calculatorConfig).front_colors_variable || '-'}</span>
            <span>Dorso: {normalizeOffsetSimpleConfigForEditor(calculatorConfig).back_colors_variable || '-'}</span>
            <span>Modo: {normalizeOffsetSimpleConfigForEditor(calculatorConfig).plate_mode_variable || '-'}</span>
            <span>Resultado: {normalizeOffsetSimpleConfigForEditor(calculatorConfig).plates_total_variable || '-'}</span>
          </div>
          )}
        </div>
        )}

        {isPageCalculatorType(calculatorType) && (
        <div className="card editor-card">
          <button
            type="button"
            className="settings-section-header editor-section-header"
            onClick={() => toggleEditorSection('calculator')}
          >
            <span>
              <span className="settings-section-title">Calculador {getPageCalculatorMeta(calculatorType).label}</span>
              <span className="settings-section-subtitle">
                {calculatorType === PRODUCT_CALCULATOR_CUADERNO
                  ? 'Hojas, tirada del interior y una chapa generada automaticamente.'
                  : 'Paginas, tirada por interior y chapas generadas automaticamente.'}
              </span>
            </span>
            <span className="settings-section-toggle">
              {openEditorSections.calculator ? 'Guardar' : 'Desplegar'}
            </span>
          </button>

          {openEditorSections.calculator ? (
          <div className="settings-section-body">
            <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
              {calculatorType === PRODUCT_CALCULATOR_CUADERNO
                ? 'La imposicion define automaticamente cuantas hojas entran por pliego. Cuaderno usa un solo interior repetitivo y una sola chapa.'
                : 'La imposicion define automaticamente cuantas paginas entran por pliego. Activa solo los interiores reales del producto.'}
            </p>
            <div className="variable-row">
              <div className="form-group">
                <label className="form-label">
                  {calculatorType === PRODUCT_CALCULATOR_CUADERNO ? 'Hojas por pliego' : 'Paginas por pliego'}
                </label>
                <input
                  className="form-input"
                  value={calculatorConfig.pages_per_sheet_variable || DEFAULT_IMPOSITION_OUTPUT_VARIABLES.pagesPerSheet}
                  readOnly
                />
                <div className="input-hint">Sale de la imposicion elegida en el presupuesto.</div>
              </div>
              <div className="form-group">
                <label className="form-label">
                  {calculatorType === PRODUCT_CALCULATOR_CUADERNO ? 'Variable total de hojas' : 'Variable total de paginas'}
                </label>
                <input
                  className="form-input"
                  value={calculatorConfig.page_count_variable || getPageCalculatorDefaults(calculatorType).page_count_variable || 'paginas'}
                  readOnly
                />
                <div className="input-hint">
                  {calculatorType === PRODUCT_CALCULATOR_CUADERNO
                    ? 'Se toma de la unica variable de hojas del interior.'
                    : 'Se genera sumando las paginas de los interiores activos.'}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Caras por pliego</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={calculatorConfig.faces_per_sheet}
                  min="1"
                  step="1"
                  onChange={(e) => setCalculatorConfig({ ...calculatorConfig, faces_per_sheet: e.target.value })}
                />
              </div>
            </div>

            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #EEEEEE' }}>
              <p className="form-label" style={{ marginBottom: '0.75rem' }}>
                {calculatorType === PRODUCT_CALCULATOR_CUADERNO ? 'Interior' : 'Interiores'}
              </p>
              {normalizePageElementsForEditor(calculatorType, calculatorConfig.elements).map((element, index) => (
                <div key={`magazine-element-${index}`} className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
                  <div className="form-section-header" style={{ marginBottom: '1rem' }}>
                    {calculatorType === PRODUCT_CALCULATOR_CUADERNO ? (
                      <strong>{element.label || 'Interior'}</strong>
                    ) : (
                      <label className="checkbox-label" style={{ margin: 0 }}>
                        <input
                          type="checkbox"
                          checked={element.enabled}
                          onChange={(e) => updateMagazineElement(index, { enabled: e.target.checked })}
                        />
                        {' '}{element.label || `Interior ${index + 1}`}
                      </label>
                    )}
                  </div>
                  {element.enabled && (
                    <>
                      <div className="variable-row">
                        <div className="form-group">
                          <label className="form-label">
                            {calculatorType === PRODUCT_CALCULATOR_CUADERNO ? 'Variable hojas' : 'Variable paginas'}
                          </label>
                          <select
                            className="form-input"
                            value={element.page_count_variable}
                            onChange={(e) => updateMagazineElement(index, { page_count_variable: e.target.value })}
                          >
                            <option value="">-- Elegir --</option>
                            {inputNumericVariables.map(v => (
                              <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                                {v.label || v.key}{v.global ? ' (global)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">
                            {calculatorType === PRODUCT_CALCULATOR_CUADERNO ? 'Colores interior' : 'Colores frente'}
                          </label>
                          <select
                            className="form-input"
                            value={element.front_colors_variable}
                            onChange={(e) => updateMagazineElement(index, { front_colors_variable: e.target.value })}
                          >
                            <option value="">-- Elegir --</option>
                            {inputNumericVariables.map(v => (
                              <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                                {v.label || v.key}{v.global ? ' (global)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        {calculatorType !== PRODUCT_CALCULATOR_CUADERNO && (
                          <div className="form-group">
                            <label className="form-label">Colores dorso</label>
                            <select
                              className="form-input"
                              value={element.back_colors_variable}
                              onChange={(e) => updateMagazineElement(index, { back_colors_variable: e.target.value })}
                            >
                              <option value="">-- Elegir --</option>
                              {inputNumericVariables.map(v => (
                                <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                                  {v.label || v.key}{v.global ? ' (global)' : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      <div className="settings-section-summary editor-section-summary">
                        <span>Genera {element.distinct_sheets_variable}</span>
                        <span>{element.faces_variable}</span>
                        <span>{element.plates_variable}</span>
                        <span>{element.print_sheets_variable}</span>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #EEEEEE' }}>
              <p className="form-label" style={{ marginBottom: '0.75rem' }}>Variables generadas por {getPageCalculatorMeta(calculatorType).label}</p>
              <div className="variable-row">
                <div className="form-group">
                  <label className="form-label">Variable de pliegos distintos</label>
                  <select
                    className="form-input"
                    value={calculatorConfig.distinct_sheets_variable}
                    onChange={(e) => setCalculatorConfig({ ...calculatorConfig, distinct_sheets_variable: e.target.value })}
                  >
                    {numericVariables.map(v => (
                      <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                        {v.label || v.key}{v.global ? ' (global)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Variable de chapas</label>
                  <select
                    className="form-input"
                    value={calculatorConfig.plates_variable}
                    onChange={(e) => setCalculatorConfig({ ...calculatorConfig, plates_variable: e.target.value })}
                  >
                    {numericVariables.map(v => (
                      <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                        {v.label || v.key}{v.global ? ' (global)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Variable de caras</label>
                  <select
                    className="form-input"
                    value={calculatorConfig.faces_variable}
                    onChange={(e) => setCalculatorConfig({ ...calculatorConfig, faces_variable: e.target.value })}
                  >
                    {numericVariables.map(v => (
                      <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                        {v.label || v.key}{v.global ? ' (global)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                type="button"
                className="btn-small"
                onClick={() => ensureMagazineOutputVariables(calculatorConfig)}
              >
                Generar variables
              </button>
            </div>
          </div>
          ) : (
          <div className="settings-section-summary editor-section-summary">
            <span>{calculatorType === PRODUCT_CALCULATOR_CUADERNO ? 'Hojas' : 'Paginas'}: {calculatorConfig.page_count_variable || '-'}</span>
            <span>{calculatorType === PRODUCT_CALCULATOR_CUADERNO ? 'Hojas/pliego' : 'Paginas/pliego'}: {calculatorConfig.pages_per_sheet_variable || '-'}</span>
            <span>Chapas: {calculatorConfig.plates_variable || '-'}</span>
          </div>
          )}
        </div>
        )}

        {calculatorType === PRODUCT_CALCULATOR_FACTURAS && (
        <div className="card editor-card">
          <button
            type="button"
            className="settings-section-header editor-section-header"
            onClick={() => toggleEditorSection('calculator')}
          >
            <span>
              <span className="settings-section-title">Calculador Facturas</span>
              <span className="settings-section-subtitle">
                Copias, papeles, colores y chapas manuales por cada cuerpo.
              </span>
            </span>
            <span className="settings-section-toggle">
              {openEditorSections.calculator ? 'Guardar' : 'Desplegar'}
            </span>
          </button>

          {openEditorSections.calculator ? (
          <div className="settings-section-body">
            <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
              El usuario carga el tipo de factura en el presupuesto. El calculador suma solo las copias activas y agrupa la impresion cuando cambian los papeles.
            </p>
            <div className="variable-row">
              <div className="form-group">
                <label className="form-label">Variable tipo de factura</label>
                <select
                  className="form-input"
                  value={normalizeInvoiceConfigForEditor(calculatorConfig).copy_count_variable}
                  onChange={(e) => setCalculatorConfig({
                    ...normalizeInvoiceConfigForEditor(calculatorConfig),
                    copy_count_variable: e.target.value,
                  })}
                >
                  <option value="">-- Elegir --</option>
                  {inputNumericVariables.map(v => (
                    <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                      {v.label || v.key}{v.global ? ' (global)' : ''}
                    </option>
                  ))}
                </select>
                <div className="input-hint">Debe valer 1, 2, 3 o 4 para Original, Duplicado, Triplicado o Cuatriplicado.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Variable total chapas</label>
                <input className="form-input" value={normalizeInvoiceConfigForEditor(calculatorConfig).plates_output_variable} readOnly />
              </div>
              <div className="form-group">
                <label className="form-label">Variable grupos impresion</label>
                <input className="form-input" value={normalizeInvoiceConfigForEditor(calculatorConfig).print_groups_output_variable} readOnly />
              </div>
            </div>

            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #EEEEEE' }}>
              <p className="form-label" style={{ marginBottom: '0.75rem' }}>Configuracion de copias</p>
              {normalizeInvoiceConfigForEditor(calculatorConfig).copies.map((copy, index) => {
                const valueVariables = allVariables.filter(v => v.key && ['number', 'select'].includes(v.type));
                return (
                  <div key={`invoice-copy-${index}`} className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
                    <div className="form-section-header" style={{ marginBottom: '1rem' }}>
                      <strong>{copy.label || INVOICE_COPY_LABELS[index]}</strong>
                    </div>
                    <div className="variable-row">
                      <div className="form-group">
                        <label className="form-label">Papel</label>
                        <select
                          className="form-input"
                          value={copy.paper_variable}
                          onChange={(e) => updateInvoiceCopy(index, { paper_variable: e.target.value })}
                        >
                          <option value="">-- Elegir --</option>
                          {valueVariables.map(v => (
                            <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                              {v.label || v.key}{v.global ? ' (global)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Gramaje</label>
                        <select
                          className="form-input"
                          value={copy.grammage_variable}
                          onChange={(e) => updateInvoiceCopy(index, { grammage_variable: e.target.value })}
                        >
                          <option value="">-- Elegir --</option>
                          {inputNumericVariables.map(v => (
                            <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                              {v.label || v.key}{v.global ? ' (global)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Colores</label>
                        <select
                          className="form-input"
                          value={copy.colors_variable}
                          onChange={(e) => updateInvoiceCopy(index, { colors_variable: e.target.value })}
                        >
                          <option value="">-- Elegir --</option>
                          {inputNumericVariables.map(v => (
                            <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                              {v.label || v.key}{v.global ? ' (global)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Chapas</label>
                        <select
                          className="form-input"
                          value={copy.plates_variable}
                          onChange={(e) => updateInvoiceCopy(index, { plates_variable: e.target.value })}
                        >
                          <option value="">-- Elegir --</option>
                          {inputNumericVariables.map(v => (
                            <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                              {v.label || v.key}{v.global ? ' (global)' : ''}
                            </option>
                          ))}
                        </select>
                        <div className="input-hint">La cantidad de chapas la carga el usuario; no se calcula sola.</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="btn-small"
              onClick={() => ensureInvoiceOutputVariables(calculatorConfig)}
            >
              Generar variables
            </button>
          </div>
          ) : (
          <div className="settings-section-summary editor-section-summary">
            <span>Tipo: {normalizeInvoiceConfigForEditor(calculatorConfig).copy_count_variable || '-'}</span>
            <span>Chapas: {normalizeInvoiceConfigForEditor(calculatorConfig).plates_output_variable || '-'}</span>
            <span>Copias configuradas: 4</span>
          </div>
          )}
        </div>
        )}

        {/* ===== FORMULA ===== */}
        <div className="card editor-card">
          <button
            type="button"
            className="settings-section-header editor-section-header"
            onClick={() => toggleEditorSection('formula')}
          >
            <span>
              <span className="settings-section-title">Formula de Calculo</span>
              <span className="settings-section-subtitle">
                Expresion de precio y variables disponibles.
              </span>
            </span>
            <span className="settings-section-toggle">
              {openEditorSections.formula ? 'Guardar' : 'Desplegar'}
            </span>
          </button>

          {openEditorSections.formula ? (
          <div className="settings-section-body">
          <div className="form-group">
            <label className="form-label">Expresion matematica</label>
            <textarea
              className="form-input mono formula-input"
              value={formula}
              onChange={(e) => setFormula(e.target.value)}
              placeholder="(largo * ancho / 10000) * papel * cantidad"
              rows={3}
              data-testid="product-formula-input"
            />
            <div className="input-hint">
              Usa los identificadores de las variables. Operadores permitidos: + - * / ( )
            </div>
          </div>

          {formulaReferenceVariables.length > 0 && (
            <div className="variables-reference">
              <div className="form-label">Variables disponibles:</div>
              <div className="variables-tags">
                {formulaReferenceVariables.filter(v => v.key).map(v => (
                  <span key={`${v.global ? 'global' : v.generated ? 'generated' : 'product'}-${v.key}`} className="variable-tag mono">
                    {v.key}{v.global ? ' (global)' : ''}{v.generated ? ' (auto)' : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
          </div>
          ) : (
          <div className="settings-section-summary editor-section-summary">
            <span>{formula.trim() ? `${formula.trim().length} caracteres de formula` : 'Sin formula'}</span>
            <span>{formulaReferenceVariables.filter(v => v.key).length} variables disponibles</span>
          </div>
          )}
        </div>

        {/* ===== PDF DETAIL ===== */}
        <div className="card editor-card">
          <button
            type="button"
            className="settings-section-header editor-section-header"
            onClick={() => toggleEditorSection('pdfDetail')}
          >
            <span>
              <span className="settings-section-title">Detalle del PDF</span>
              <span className="settings-section-subtitle">
                Variables y texto extra que aparecen en la columna Detalle del presupuesto.
              </span>
            </span>
            <span className="settings-section-toggle">
              {openEditorSections.pdfDetail ? 'Guardar' : 'Desplegar'}
            </span>
          </button>

          {openEditorSections.pdfDetail ? (
          <div className="settings-section-body">
            <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
              Marca solo la informacion comercial que queres imprimir. Las variables de costo pueden quedar fuera del PDF.
            </p>

            {pdfDetailVariableCandidates.length === 0 ? (
              <div className="empty-state">
                Todavia no hay variables disponibles para mostrar en el detalle.
              </div>
            ) : (
              <div className="pdf-detail-variable-list">
                {pdfDetailVariableCandidates.map(v => (
                  <label key={`${v.global ? 'global' : 'product'}-${v.key}`} className="pdf-detail-variable-option">
                    <input
                      type="checkbox"
                      checked={(pdfDetailConfig.variable_keys || []).includes(v.key)}
                      onChange={() => togglePdfDetailVariable(v.key)}
                    />
                    <span>
                      <strong>{v.label || v.key}</strong>
                      <small>
                        {v.key}{v.global ? ' - global' : ''}{v.unit ? ` - ${v.unit}` : ''}
                      </small>
                    </span>
                  </label>
                ))}
              </div>
            )}

            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label">Texto extra del detalle</label>
              <textarea
                className="form-input"
                value={pdfDetailConfig.extra_text || ''}
                onChange={(e) => setPdfDetailConfig({ ...pdfDetailConfig, extra_text: e.target.value })}
                placeholder="Ej: En ilustracion brillante de 90 gr, 4 colores de frente"
                rows={3}
              />
              <div className="input-hint">
                Este texto se agrega al detalle del PDF para este producto.
              </div>
            </div>
          </div>
          ) : (
          <div className="settings-section-summary editor-section-summary">
            <span>{(pdfDetailConfig.variable_keys || []).length} variables en detalle</span>
            <span>{pdfDetailConfig.extra_text ? 'Con texto extra' : 'Sin texto extra'}</span>
          </div>
          )}
        </div>

        {/* ===== IMPOSITION ===== */}
        {productionType && supportsOffsetImposition ? (
        <div className="card editor-card">
          <button
            type="button"
            className="settings-section-header editor-section-header"
            onClick={() => toggleEditorSection('imposition')}
          >
            <span>
              <span className="settings-section-title">Configuracion de Imposicion</span>
              <span className="settings-section-subtitle">
                Pliego, pinzas y variables automaticas de hoja madre.
              </span>
            </span>
            <span className="settings-section-toggle">
              {openEditorSections.imposition ? 'Guardar' : 'Desplegar'}
            </span>
          </button>

          {openEditorSections.imposition ? (
          <div className="settings-section-body">
          <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
            Obligatorio para productos Offset. Calcula pliegos, hoja madre y datos de imposicion para usarlos en la formula de precio.
          </p>

            <>
              <div className="imposition-controls">
                <div className="form-group">
                  <label className="form-label">Ancho pliego (cm)</label>
                  <input type="number" className="form-input mono" value={impositionConfig.sheet_width}
                    onChange={(e) => setImpositionConfig({ ...impositionConfig, sheet_width: e.target.value })}
                    step="0.1" data-testid="prod-imp-sheet-width" />
                </div>
                <div className="form-group">
                  <label className="form-label">Alto pliego (cm)</label>
                  <input type="number" className="form-input mono" value={impositionConfig.sheet_height}
                    onChange={(e) => setImpositionConfig({ ...impositionConfig, sheet_height: e.target.value })}
                    step="0.1" data-testid="prod-imp-sheet-height" />
                </div>
                <div className="form-group">
                  <label className="form-label">Pinza superior (cm)</label>
                  <input type="number" className="form-input mono" value={impositionConfig.gripper_top}
                    onChange={(e) => setImpositionConfig({ ...impositionConfig, gripper_top: e.target.value })}
                    step="0.1" data-testid="prod-imp-gripper-top" />
                </div>
                <div className="form-group">
                  <label className="form-label">Pinza inferior (cm)</label>
                  <input type="number" className="form-input mono" value={impositionConfig.gripper_bottom}
                    onChange={(e) => setImpositionConfig({ ...impositionConfig, gripper_bottom: e.target.value })}
                    step="0.1" />
                </div>
                <div className="form-group">
                  <label className="form-label">Sangrado default (cm)</label>
                  <input type="number" className="form-input mono" value={impositionConfig.bleed}
                    onChange={(e) => setImpositionConfig({ ...impositionConfig, bleed: e.target.value })}
                    step="0.1" data-testid="prod-imp-bleed" />
                </div>
                <div className="form-group">
                  <label className="form-label">Separacion default (cm)</label>
                  <input type="number" className="form-input mono" value={impositionConfig.separation}
                    onChange={(e) => setImpositionConfig({ ...impositionConfig, separation: e.target.value })}
                    step="0.1" />
                </div>
                <div className="form-group">
                  <label className="form-label">Desperdicio fijo (hojas madre)</label>
                  <input type="number" className="form-input mono" value={impositionConfig.spoilage_sheets}
                    onChange={(e) => setImpositionConfig({ ...impositionConfig, spoilage_sheets: e.target.value })}
                    step="1" min="0" />
                </div>
              </div>

              <div className="variable-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={impositionConfig.allow_rotation}
                      onChange={(e) => setImpositionConfig({ ...impositionConfig, allow_rotation: e.target.checked })} />
                    {' '}Permitir rotacion default
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={impositionConfig.double_sided}
                      onChange={(e) => setImpositionConfig({ ...impositionConfig, double_sided: e.target.checked })} />
                    {' '}Doble faz default
                  </label>
                </div>
              </div>

              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #EEEEEE' }}>
                <p className="form-label" style={{ marginBottom: '0.75rem' }}>Mapeo de Variables</p>
                <p className="dashboard-subtitle" style={{ marginBottom: '1rem', fontSize: '0.85rem' }}>
                  Indica que variables numericas representan el ancho, alto y cantidad de la pieza.
                </p>
                <div className="variable-row">
                  <div className="form-group">
                    <label className="form-label">Variable de ancho</label>
                    <select className="form-input" value={impositionConfig.width_variable}
                      onChange={(e) => setImpositionConfig({ ...impositionConfig, width_variable: e.target.value })}
                      data-testid="prod-imp-width-var">
                      <option value="">-- Elegir --</option>
                      {inputNumericVariables.map(v => (
                        <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                          {v.label || v.key}{v.global ? ' (global)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Variable de alto</label>
                    <select className="form-input" value={impositionConfig.height_variable}
                      onChange={(e) => setImpositionConfig({ ...impositionConfig, height_variable: e.target.value })}
                      data-testid="prod-imp-height-var">
                      <option value="">-- Elegir --</option>
                      {inputNumericVariables.map(v => (
                        <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                          {v.label || v.key}{v.global ? ' (global)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Variable de cantidad</label>
                    <select className="form-input" value={impositionConfig.quantity_variable}
                      onChange={(e) => setImpositionConfig({ ...impositionConfig, quantity_variable: e.target.value })}
                      data-testid="prod-imp-quantity-var">
                      <option value="">-- Elegir --</option>
                      {inputNumericVariables.map(v => (
                        <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                          {v.label || v.key}{v.global ? ' (global)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <p className="form-label" style={{ margin: '1rem 0 0.75rem' }}>Variables completadas por imposicion</p>
                <p className="dashboard-subtitle" style={{ marginBottom: '1rem', fontSize: '0.85rem' }}>
                  Estas variables se actualizan solas al elegir una opcion de pliego. Usalas en la formula del producto.
                </p>
                <div className="variable-row">
                  <div className="form-group">
                    <label className="form-label">Variable de pliegos</label>
                    <select className="form-input" value={impositionConfig.sheets_variable}
                      onChange={(e) => setImpositionConfig({ ...impositionConfig, sheets_variable: e.target.value })}>
                      <option value="">-- Elegir --</option>
                      {numericVariables.map(v => (
                        <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                          {v.label || v.key}{v.global ? ' (global)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Variable de hojas madre</label>
                    <select className="form-input" value={impositionConfig.mother_sheet_variable}
                      onChange={(e) => setImpositionConfig({ ...impositionConfig, mother_sheet_variable: e.target.value })}>
                      <option value="">-- Elegir --</option>
                      {numericVariables.map(v => (
                        <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                          {v.label || v.key}{v.global ? ' (global)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Variable de desperdicio</label>
                    <select className="form-input" value={impositionConfig.spoilage_variable}
                      onChange={(e) => setImpositionConfig({ ...impositionConfig, spoilage_variable: e.target.value })}>
                      <option value="">-- Elegir --</option>
                      {numericVariables.map(v => (
                        <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                          {v.label || v.key}{v.global ? ' (global)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Variable de ancho hoja</label>
                    <select className="form-input" value={impositionConfig.sheet_width_variable}
                      onChange={(e) => setImpositionConfig({ ...impositionConfig, sheet_width_variable: e.target.value })}>
                      <option value="">-- Elegir --</option>
                      {numericVariables.map(v => (
                        <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                          {v.label || v.key}{v.global ? ' (global)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Variable de alto hoja</label>
                    <select className="form-input" value={impositionConfig.sheet_height_variable}
                      onChange={(e) => setImpositionConfig({ ...impositionConfig, sheet_height_variable: e.target.value })}>
                      <option value="">-- Elegir --</option>
                      {numericVariables.map(v => (
                        <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                          {v.label || v.key}{v.global ? ' (global)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      Variable {calculatorType === PRODUCT_CALCULATOR_CUADERNO ? 'hojas' : 'paginas'} por pliego
                    </label>
                    <select className="form-input" value={impositionConfig.pages_per_sheet_variable}
                      onChange={(e) => {
                        const nextKey = e.target.value;
                        setImpositionConfig({ ...impositionConfig, pages_per_sheet_variable: nextKey });
                        setCalculatorConfig(current => ({ ...current, pages_per_sheet_variable: nextKey }));
                      }}>
                      <option value="">-- Elegir --</option>
                      {numericVariables.map(v => (
                        <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                          {v.label || v.key}{v.global ? ' (global)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </>
          </div>
          ) : (
          <div className="settings-section-summary editor-section-summary">
            <span>Pliego {impositionConfig.sheet_width || 0}x{impositionConfig.sheet_height || 0} cm</span>
            <span>Pliegos: {impositionConfig.sheets_variable || '-'}</span>
            <span>Hojas madre: {impositionConfig.mother_sheet_variable || '-'}</span>
            <span>Desperdicio: {impositionConfig.spoilage_sheets || 0} hojas</span>
          </div>
          )}
        </div>
        ) : productionType ? (
        <div className="card editor-card">
          <h3 className="card-title">Configuracion de Imposicion</h3>
          <div className="empty-state">
            {selectedProductionModel.label} no usa la configuracion de imposicion de Offset. Su logica queda aislada para implementarla en su propio modelo.
          </div>
        </div>
        ) : null}

        {/* ===== PRODUCTION TIME ===== */}
        {productionType && supportsProductionTime ? (
        <div className="card editor-card">
          <button
            type="button"
            className="settings-section-header editor-section-header"
            onClick={() => toggleEditorSection('productionTime')}
          >
            <span>
              <span className="settings-section-title">Tiempo de Produccion</span>
              <span className="settings-section-subtitle">
                Perfil de maquina y variable que genera el tiempo estimado.
              </span>
            </span>
            <span className="settings-section-toggle">
              {openEditorSections.productionTime ? 'Guardar' : 'Desplegar'}
            </span>
          </button>

          {openEditorSections.productionTime ? (
          <div className="settings-section-body">
          <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
            Obligatorio para productos Offset. Genera las variables automaticas tiempo y tiempo_minutos para usarlas en la formula de precio.
          </p>

            <div className="variable-row">
              <div className="form-group">
                <label className="form-label">Perfil de produccion</label>
                <select
                  className="form-input"
                  value={productionTimeConfig.profile_key}
                  onChange={(e) => setProductionTimeConfig({ ...productionTimeConfig, profile_key: e.target.value })}
                >
                  {(productionProfiles.length ? productionProfiles : [{ key: 'offset_principal', label: 'Offset principal' }]).map(profile => (
                    <option key={profile.key} value={profile.key}>{profile.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Variable de cantidad a imprimir</label>
                <select
                  className="form-input"
                  value={productionTimeConfig.quantity_variable}
                  onChange={(e) => setProductionTimeConfig({ ...productionTimeConfig, quantity_variable: e.target.value })}
                >
                  <option value="">-- Elegir --</option>
                  {numericVariables.map(v => (
                    <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                      {v.label || v.key}{v.global ? ' (global)' : ''}
                    </option>
                  ))}
                </select>
                <div className="input-hint">Para productos con imposicion, usa la variable automatica de pliegos.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Multiplicador por defecto</label>
                <input
                  type="number"
                  className="form-input mono"
                  value={productionTimeConfig.default_sides_multiplier}
                  onChange={(e) => setProductionTimeConfig({ ...productionTimeConfig, default_sides_multiplier: e.target.value })}
                  step="1"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Variable multiplicadora opcional</label>
                <select
                  className="form-input"
                  value={productionTimeConfig.sides_multiplier_variable}
                  onChange={(e) => setProductionTimeConfig({ ...productionTimeConfig, sides_multiplier_variable: e.target.value })}
                >
                  <option value="">Sin variable</option>
                  {numericVariables.map(v => (
                    <option key={`${v.global ? 'global' : 'product'}-${v.key}`} value={v.key}>
                      {v.label || v.key}{v.global ? ' (global)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          ) : (
          <div className="settings-section-summary editor-section-summary">
            <span>{selectedProductionProfile?.label || productionTimeConfig.profile_key || 'Sin perfil'}</span>
            <span>Cantidad: {productionTimeConfig.quantity_variable || '-'}</span>
            <span>Multiplicador: {productionTimeConfig.sides_multiplier_variable || productionTimeConfig.default_sides_multiplier || 1}</span>
          </div>
          )}
        </div>
        ) : productionType ? (
        <div className="card editor-card">
          <h3 className="card-title">Tiempo de Produccion</h3>
          <div className="empty-state">
            {selectedProductionModel.label} todavia no tiene configuracion de tiempo. Se agregara en su propio calculador sin afectar Offset.
          </div>
        </div>
        ) : null}

        {/* ===== SAVE ===== */}
        <div className="editor-actions">
          {saveError && <div className="login-error" data-testid="save-error">{saveError}</div>}
          <div className="editor-buttons">
            <button className="btn-secondary" onClick={() => navigate('/productos')}>Cancelar</button>
            <button
              className="btn-primary-inline"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-product-button"
            >
              {saving ? 'Guardando...' : (isEdit ? 'Guardar Cambios' : 'Crear Producto')}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProductEditor;
